import { friendlyErrorMessage } from "./error-copy.ts";

// ── Shared types ──────────────────────────────────────────────────

export interface Session {
  user: {
    id: string;
    name: string;
    email: string;
    image?: string;
    role?: string;
  };
  session: { id: string; activeOrganizationId: string | null };
}

export interface OrgRow {
  id: string;
  name: string;
  slug: string | null;
  logo: string | null;
  createdAt: string;
  metadata: string | null;
  website: string | null;
  /** Login methods this org's members may use; null = all methods. Only
   * present for super-admins (field-policy gated). */
  allowedAuthMethods?: string[] | null;
  /** active | test | dormant — anything but active is skipped by billing
   * Slack alerts. Only present for super-admins (field-policy gated). */
  status?: OrgStatus;
}

export const ORG_STATUSES = ["active", "test", "dormant"] as const;
export type OrgStatus = (typeof ORG_STATUSES)[number];

export interface OrgMember {
  id: string;
  userId: string;
  role: string;
  createdAt: string;
  userName: string;
  userEmail: string;
}

export interface UserRow {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  image: string | null;
  role: string | null;
  banned: boolean | null;
  banReason: string | null;
  createdAt: string;
  lastSignIn: string | null;
}

export interface AdminOrgEnriched extends OrgRow {
  memberCount: number;
  containerCount: number;
  creditBalance: number;
  ownerName: string | null;
  ownerEmail: string | null;
  lifetimeSpend: number;
  lifetimeGranted: number;
  lastActivityAt: string | null;
  consumptionSparkline: number[];
}

export interface AdminContainerRow {
  id: string;
  name: string;
  agentType: string;
  deploymentStatus: string;
  // `railway`, a cluster name, or `self-hosted`. Missing from a backend that predates it.
  hostingTarget?: string;
  errorMessage: string | null;
  createdAt: string;
  taskCount: number;
  lastActivityAt: string | null;
}

export interface InvitationRow {
  id: string;
  email: string;
  role: string | null;
  status: string;
  expiresAt: string;
  inviterId: string;
  createdAt: string;
  emailError: string | null;
}

export interface InfrastructureOverview {
  warmPool: {
    enabled: boolean;
    pools: Record<
      string,
      { available: number; provisioning: number; minimum: number }
    >;
  };
  fleet: {
    byStatus: Record<string, number>;
    byType: Record<string, number>;
    total: number;
  };
  containers: Array<{
    id: string;
    name: string;
    organizationId: string;
    organizationName: string;
    agentType: string;
    deploymentStatus: string;
    errorMessage: string | null;
    sandboxProvider: SandboxProvider;
    lastActivity: string | null;
    createdAt: string;
    updatedAt: string;
    idle: boolean;
  }>;
  poolContainers: Array<{
    id: string;
    name: string;
    agentType: string;
    deploymentStatus: string;
    createdAt: string;
  }>;
  poolBelowMinimum: boolean;
}

export interface TeardownPreflight {
  container: { id: string; name: string; agentType: string; status: string };
  // The exact resources this teardown will delete — human label, the resource
  // id, and an optional dashboard url (Railway / Slack) to verify before deleting.
  willDelete: Array<{ label: string; id: string; url?: string }>;
  // Customer-owned surfaces teardown does NOT remove — orphaned unless deleted
  // by hand (Teams/GChat apps, Hub-connected accounts). Optional: absent during
  // a deploy window where the frontend is live before the backend ships it.
  manual?: Array<{ type: string; detail: string; whereToDelete: string }>;
  _permissions?: Record<string, boolean> | null;
}

// Precondition check for org teardown. An org can only be soft-deleted once all
// of its Boos are torn down; `liveContainers` is the blocking set (empty ⇒
// `canDelete`). `_permissions.canDelete` gates the button (super-admin only).
export interface OrgTeardownPreflight {
  org: { id: string; name: string };
  liveContainers: Array<{
    id: string;
    name: string;
    agentType: string;
    status: string;
  }>;
  canDelete: boolean;
  _permissions?: Record<string, boolean> | null;
}

export interface AdminContainerDetail {
  id: string;
  name: string;
  description: string | null;
  agentType: string;
  organizationId: string;
  organizationName: string;
  organizationSlug: string | null;
  status: string;
  deploymentStatus: string;
  errorMessage: string | null;
  currency: string;
  manifestVersion: string | null;
  releaseChannel: ReleaseChannel;
  // `railway`, a cluster name, or `self-hosted`. Missing from a backend that predates it.
  hostingTarget?: string;
  sandboxProvider: SandboxProvider;
  vercelProjectId: string | null;
  sandboxProviderChangedAt: string | null;
  sandboxProviderChangedBy: string | null;
  railwayProjectId: string | null;
  railwayEnvironmentId: string | null;
  railwayServiceId: string | null;
  railwayServiceUrl: string | null;
  brainVolumeId: string | null;
  redisServiceId: string | null;
  redisVolumeId: string | null;
  homeVolumeId: string | null;
  workspaceVolumeId: string | null;
  customDomainFqdn: string | null;
  customDomainStatus: string | null;
  customDomainError: string | null;
  railwayCustomDomainId: string | null;
  cloudflareDnsRecordId: string | null;
  gatewayCredentialId: string | null;
  slackTeamId: string | null;
  slackAppId: string | null;
  teamsTenantId: string | null;
  teamsBotId: string | null;
  teamsServiceUrl: string | null;
  claimedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  // Setup as the backend's reconcile loop sees it; null for a Boo it doesn't track (Railway).
  setup?: AdminBooSetup | null;
}

export interface AdminBooSetup {
  conditions: import("./setup-progress").SetupConditions;
  lastError: string | null;
  nextRunAt: string;
  pendingEvents: Array<{ id: string; kind: string; waitFor: string[]; attempts: number; lastError: string | null }>;
  config: { state: "true" | "false" | "unknown"; version: number | null; reportedVersion: number | null; failing: string[] };
}

export interface BrainVersion {
  tag: string;
  image: string;
  kind: "release" | "rolling" | "staging";
  title: string | null;
  highlights: string[];
  notesUrl: string | null;
  publishedAt: string | null;
  resolvesTo: string | null;
}

export type ReleaseChannel = "stable" | "edge" | "pinned";

// Where a brain runs workspace sandboxes. Mirrors BOO_WORKSPACE_SANDBOX_PROVIDER
// on its Railway service.
export type SandboxProvider = "e2b" | "vercel";

export interface SandboxProviderStatus {
  sandboxProvider: SandboxProvider;
  vercelProjectId: string | null;
  // The org's own Vercel project, shared by its Boos; null until the backend
  // created one for the first Boo moved in automatic mode.
  orgVercelProjectId: string | null;
  // vercelProjectId is the org's project, so teardown of the org's last Boo
  // deletes it; a pasted one is left alone.
  vercelProjectOwned: boolean;
  vercelTokenMinted: boolean;
  sandboxProviderChangedAt: string | null;
  sandboxProviderChangedBy: string | null;
  // The backend has VERCEL_TEAM_TOKEN: moving to Vercel needs no credentials.
  vercelAutomatic: boolean;
  // What the brain's Railway env says; null when Railway couldn't be read.
  live: { provider: string | null; vercelProjectId: string | null; resetNotice: string | null } | null;
}

export interface SandboxProviderSwitch {
  provider: SandboxProvider;
  resetNotice?: string;
  vercel?: { token: string; teamId: string; projectId: string };
}

export interface SandboxProviderSwitchResult {
  id: string;
  sandboxProvider: SandboxProvider;
  vercelProjectId: string | null;
  mode: "manual" | "automatic" | null;
  sandboxProviderChangedAt: string;
  sandboxProviderChangedBy: string;
  warnings: string[];
}

export interface ContainerImage {
  image: string | null;
  latestDeploymentStatus: string | null;
  // The build the Boo itself reports from /version; null while down or restarting.
  running: { version: string; gitSha: string; builtAt: string; instanceId: string | null } | null;
  releaseChannel: ReleaseChannel;
}

// ── Per-brain model settings (super-admin only) ────────────────────
// Proxied verbatim from the brain's /internal/models routes.

export type ModelPurpose =
  | "chat"
  | "intents"
  | "subagents"
  | "heartbeats"
  | "workflow"
  | "retrospect"
  | "indexing";

export interface ModelPurposeSetting {
  purpose: ModelPurpose;
  /** Explicit "vendor/model" from the brain's models file, null when unset. */
  configured: string | null;
  /** What the brain actually runs for this purpose after fallbacks. */
  effective: string;
  source: "configured" | "chat-fallback" | "default";
}

export interface ModelSettings {
  /** missing/invalid = running on built-in defaults (invalid = recovery
   * mode: a broken models file on disk). */
  fileState: "ok" | "missing" | "invalid";
  /** Every chat-class purpose the brain knows, in canonical order. */
  purposes: ModelPurposeSetting[];
  /** configured: "off", a model, or null (the built-in default). effective:
   * what runs, null only when off. Boos from before "off" treat null as off
   * (effective null) and reject "off". */
  transcription: { configured: string | null; effective: string | null };
  /** Same shape. Absent on older brains. */
  embeddings?: { configured: string | null; effective: string | null };
  decisions?: { configured: string | null; effective: string | null };
  /** Read-only context: code-owned thread-mode model preference lists. */
  threadModes: Array<{ id: string; title: string; models: string[] }>;
}

export interface ModelCatalog {
  /** baked-fallback = live gateway catalog unreachable. */
  source: "catalog" | "baked-fallback";
  models: Array<{
    name: string;
    role: "chat" | "transcription" | "embeddings" | "decision";
    summary?: string;
  }>;
}

/** Full-replace mapping: chat required; an omitted purpose falls back to
 * chat; omitted transcription/embeddings/decisions run the built-in default
 * and "off" turns one off. */
export type ModelSettingsUpdate = { chat: string } & Partial<
  Record<Exclude<ModelPurpose, "chat"> | "transcription" | "embeddings" | "decisions", string>
>;

export interface ModelSettingsUpdateResult {
  ok: true;
  changed: boolean;
  summary?: string[];
  /** The brain restarts in-process right after responding — calls may fail
   * for a few seconds. */
  restarting?: boolean;
}

// ── Model policy: org defaults, enabled models, org endpoints, per-Boo picks ──
// Org owners/admins set the default model per purpose, turn models off for the
// whole org, and add OpenAI-compatible endpoints every Boo of the org can use.
// Each Boo's admins pick any enabled model for their Boo. Which hosted models
// exist is boo-litellm's call.

export type ModelRole = "chat" | "transcription" | "embeddings" | "decision";
export type CostTier = "economy" | "standard" | "premium" | "frontier";

export interface ModelCapabilities {
  tools?: boolean;
  caching?: boolean | "none" | "anthropic" | "openai";
  context_window?: number;
  vision?: boolean;
  anthropic_messages?: boolean;
}

/** One entry in every picker and in the Manage models list. */
export interface ModelRow {
  /** Slug: "<vendor>/<id>" for hosted models, "<org endpoint>/<id>" for custom ones. */
  name: string;
  role: ModelRole;
  source: "hosted" | "custom";
  /** Org endpoint name when source is "custom". */
  endpoint: string | null;
  /** False when the org turned it off. Pickers list enabled rows only. */
  enabled: boolean;
  /** Hosted only. */
  summary?: string;
  cost_tier?: CostTier;
  capabilities?: ModelCapabilities;
}

export interface PurposeDef {
  id: string;
  label: string;
  description: string;
  required: boolean;
  /** "chat" for purposes that follow the conversation model; null = runs the
   * Boo's built-in default when unset, and can be set to "off". */
  fallback: string | null;
  role: ModelRole;
}

export interface PurposeDefView extends PurposeDef {
  /** What runs with no pick and no org default; null for chat-class
   * purposes, which follow the conversation model. */
  defaultModel: string | null;
  /** False for jobs that run on built-in models only (coding). Absent from
   * older backends: treat coding as false, everything else as true. */
  acceptsCustom?: boolean;
}

export type VerdictStatus = "pass" | "warn" | "unavailable";
export type CheckStatus = "pass" | "warn" | "fail" | "skipped";

export interface EndpointProbeSummary {
  probeId: string;
  model: string;
  completedAt: string;
  verdicts: Array<{ purpose: string; status: VerdictStatus; note?: string }>;
}

export interface OrgEndpointView {
  name: string;
  /** Plain display name ("Acme models"); the name itself when none was set.
   * Absent from older backends. */
  label?: string | null;
  kind: "openai-compatible";
  baseUrl: string;
  models: Array<{ id: string; role: ModelRole }>;
  hasKey: boolean;
  /** A key is stored but can't be read any more: ask for it again. */
  keyNeeded: boolean;
  lastProbe: EndpointProbeSummary | null;
  updatedBy: string | null;
  updatedAt: string;
}

/** A Boo feature an org setting relies on: org endpoints as models, and the
 * disabled set. */
export type PendingBooFeature = "org_endpoints" | "disabled_models";

export interface OrgModels {
  defaults: Record<string, string>;
  disabledModels: string[];
  models: ModelRow[];
  /** null: no Boo answered, so `models` holds custom rows only. */
  catalogSource: "catalog" | "baked-fallback" | null;
  endpoints: OrgEndpointView[];
  reservedEndpointNames: string[];
  purposeDefs: PurposeDefView[];
  /** Running Boos that don't apply every org setting until they update. */
  pendingBoos: Array<{ id: string; name: string; missing: PendingBooFeature[] }>;
  updatedBy: string | null;
  updatedAt: string | null;
  _permissions: { canManage: boolean };
}

export interface OrgEndpointInput {
  /** Omitted keeps it; null or "" clears it. Max 60 characters. */
  label?: string | null;
  baseUrl: string;
  models: Array<{ id: string; role: ModelRole }>;
  /** Omit to keep the stored key. */
  apiKey?: string;
  removeKey?: boolean;
  /** A finished check of this address and key, so its result is kept. */
  probeId?: string;
}

export interface EndpointProbeRequest {
  /** A saved endpoint's name, or an unsaved one to test before saving. */
  endpoint: string | { name: string; baseUrl: string; apiKey?: string };
  model: string;
  purposes?: string[];
}

export interface EffectiveModelRow {
  purpose: string;
  model: string | null;
  provider: string | null;
  /** configured | org-default | chat-fallback | default | thread-flag | unset.
   * unset = nothing runs: the pick is "off", or a Boo from before "off" has
   * nothing picked. */
  source: string;
  /** Why this purpose can't run right now (vault_ref_unresolved, gateway_unconfigured, org_key_unavailable). */
  problem?: { code: string } | string | null;
  /** A pick or default this row passed over. */
  skipped?: { model: string; reason: "model_disabled" | "model_removed" | "key_unavailable" };
  verdict: { status: VerdictStatus; note?: string; probedAt?: string } | null;
  /** Measured since the Boo last started. */
  live: {
    since?: string;
    calls: number;
    errorRate: number | null;
    p50LatencyMs: number | null;
    costUsd: number | null;
  } | null;
}

/** A custom endpoint saved on one Boo before org endpoints existed. */
export interface BooModelProvider {
  name: string;
  kind: "openai-compatible";
  baseUrl: string;
  hasKey: boolean;
  models: string[];
  keyNeeded: boolean;
  lastProbe: EndpointProbeSummary | null;
}

export interface BooModels {
  /** The Boo's own picks (its models file), whoever made them. A purpose not
   * here follows the org default, else the built-in default; "off" turns a
   * specialty purpose off. */
  selection: Record<string, string>;
  orgDefaults: Record<string, string>;
  disabledModels: string[];
  orgEndpoints: OrgEndpointView[];
  /** Legacy custom endpoints on this Boo only (empty for almost every Boo). */
  providers: BooModelProvider[];
  brain: {
    contract: number;
    features: string[];
    fileState: "ok" | "missing" | "invalid";
    supportsProviders: boolean;
    supportsProbe: boolean;
    /** The Boo fetches org defaults (older Boos don't). */
    orgDefaultsApply: boolean;
    /** The Boo can run models from org endpoints. */
    supportsOrgEndpoints: boolean;
    /** The Boo stops running models the org turned off. */
    enforcesDisabledModels: boolean;
  } | null;
  brainError?: string;
  purposeDefs?: PurposeDefView[];
  effective?: {
    providers: Array<{ name: string; kind: string; base_url?: string; origin?: "boo" | "org"; configured: boolean; problems: Array<{ code?: string; ref?: string; message?: string }> }>;
    effective: EffectiveModelRow[];
  };
  _permissions: { canConfigure: boolean; canManageOrgModels: boolean };
}

export interface BooModelCatalog {
  source: "catalog" | "baked-fallback";
  models: ModelRow[];
  reservedProviderNames: string[];
}

export interface ModelSaveResult {
  ok: true;
  changed: boolean;
}

export interface ProbeCheck {
  id: string;
  status: CheckStatus;
  value: unknown;
  note?: string;
}

export interface ProbeReport {
  probeId: string;
  status: "running" | "completed";
  startedAt?: string;
  completedAt?: string;
  provider?: { name: string; kind: string; base_url?: string };
  model?: string;
  checks: ProbeCheck[];
  verdicts?: Array<{ purpose: string; status: VerdictStatus; note?: string }>;
}

/** Super-admin, read-only. */
export interface AdminModelPolicy {
  defaults: Record<string, string>;
  disabledModels: string[];
  endpoints: Array<{ name: string; label?: string | null; baseUrl: string; models: Array<{ id: string; role: ModelRole }>; hasKey: boolean }>;
}

export interface VercelAdminSandbox {
  name: string;
  threadId: string | null;
  provider: string | null;
  status: "pending" | "running" | "stopping" | "stopped" | "failed" | "aborted" | "snapshotting";
  persistent: boolean;
  createdAt: string;
  expiresAt: string | null;
  vcpus: number | null;
  memoryMB: number | null;
  region: string | null;
}

export interface AdminSandbox {
  sandboxId: string;
  threadId: string | null;
  provider: string | null;
  state: "running" | "paused";
  startedAt: string;
  endAt: string;
  cpuCount: number;
  memoryMB: number;
  cpuUsedPct: number | null;
  memUsedMB: number | null;
}

export type CostPeriod = "this_month" | "last_month" | "last_30d" | "last_90d";

export interface ComputeSyncHealth {
  healthy: boolean;
  lastSyncAt: string | null;
  hoursSinceLastSync: number | null;
  snapshotCount: number;
  containersCovered: number;
  totalActiveContainers: number;
}

export interface ComputeSyncResult {
  synced: number;
  errors: number;
  skipped: number;
  periodStart: string;
  periodEnd: string;
  totalRawCost: number;
  totalMarkedUpCost: number;
}

export interface AdminGchatCustomRow {
  containerId: string;
  name: string;
  organizationId: string;
  organizationName: string | null;
  customerId: string | null;
  customerName: string | null;
  connected: boolean;
  skipped?: "not-running" | "no-url" | "no-secret";
  error?: string;
}

export interface AdminGchatCustomInstalledResponse {
  total: number;
  scanned: number;
  connected: AdminGchatCustomRow[];
  errored: AdminGchatCustomRow[];
  all: AdminGchatCustomRow[];
}

export interface AdminSlackConnectionRow {
  containerId: string;
  name: string;
  organizationId: string;
  organizationName: string | null;
  connectionType: "boo" | "custom";
  teamId: string | null;
  teamName: string | null;
  slackAppId: string | null;
  botUserId: string | null;
  deploymentStatus: string | null;
}

export interface AdminSlackInstalledResponse {
  total: number;
  boo: number;
  custom: number;
  connected: AdminSlackConnectionRow[];
}

export interface AdminNamedWarmPoolRow {
  id: string;
  companyName: string;
  reservedSubdomain: string;
  domains: string[];
  expectedFirstUserEmail: string | null;
  containerId: string | null;
  claimedByOrgId: string | null;
  claimedByOrgName: string | null;
  claimedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  // From the joined container: branded URL + provisioning/cert status.
  containerStatus: string | null;
  customDomainStatus: string | null;
  customDomainFqdn: string | null;
  railwayServiceUrl: string | null;
}

export interface CreateNamedWarmPoolBody {
  companyName: string;
  reservedSubdomain: string;
  domains: string[];
  expectedFirstUserEmail?: string;
  notes?: string;
}

// Dashboard chat with a boo-brain (super-admin only, gated in the backend).
// The transcript lives in brain memory (cleared on brain restart), so these
// are live reads, not an archive.
export interface BrainChatThread {
  threadKey: string;
  title: string;
  interactive: boolean;
  messageCount: number;
  lastMessageAt: number;
}

export interface VoiceOption {
  id: string;
  name: string;
  category: string;
  labels: Record<string, string>;
  description: string | null;
  // The provider's stock sample clip, when boo-tts exposes one.
  previewUrl: string | null;
}

export interface BrainChatEscalation {
  escalationId: string;
  status: "pending" | "approved" | "denied" | "mixed";
  toolCalls: { toolCallId: string; toolName: string; reason: string }[];
  resolvedBy?: string;
}

export interface BrainChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  createdAt: number;
  // Typed rows: an approval prompt, or the out-of-credits stop.
  kind?: "escalation" | "credits";
  escalation?: BrainChatEscalation;
  credits?: { topupUrl: string };
  // Files the user attached to this message (metadata only).
  attachments?: { name: string; mimeType: string; size: number }[];
}

export type BrainChatDecision = "approve_all" | "deny_all" | { decisions: { toolCallId: string; decision: "approve" | "deny" }[] };

export interface LLMUsageOverview {
  period: { start: string; end: string };
  totals: {
    calls: number;
    tokens: number;
    // What upstream charged us (gateway-side `llm_requests.costUsd`).
    rawCostUsd: number;
    // What we billed customers (boo-backend `llm_consumption_log.markedUpCostUsd`).
    // Spread vs rawCostUsd is our margin.
    markedUpCostUsd: number;
    sessions: number;
    containers: number;
  };
  byContainer: Array<{
    containerId: string | null;
    containerName: string;
    organizationId: string | null;
    organizationName: string | null;
    calls: number;
    tokens: number;
    rawCostUsd: number;
    markedUpCostUsd: number;
  }>;
  byOrg: Array<{
    organizationId: string | null;
    organizationName: string | null;
    calls: number;
    tokens: number;
    rawCostUsd: number;
    markedUpCostUsd: number;
  }>;
  byModel: Array<{
    model: string | null;
    provider: string | null;
    calls: number;
    tokens: number;
    rawCostUsd: number;
    markedUpCostUsd: number;
  }>;
}

export interface AdminCosts {
  period: { start: string; end: string };
  aiCost: number;
  computeCost: number;
  infraCost: number;
  // BOO-266: platform storage spend (per-container/bundle disk+backup plus infra
  // volume+backup) and the infra-service backup slice.
  storageCost: number;
  infraBackupCost: number;
  // Each paid service broken out on its own (not merged).
  searchCost: number;
  searchByContainer: Array<{
    containerId: string;
    containerName: string;
    cost: number;
  }>;
  sandboxCost: number;
  sandboxByContainer: Array<{
    containerId: string;
    containerName: string;
    cost: number;
  }>;
  // Active Boos on Vercel Sandbox: their sandbox spend isn't synced (E2B only).
  // Absent from a backend that predates the field.
  vercelSandboxContainers?: Array<{ containerId: string; containerName: string }>;
  imageCost: number;
  imageByContainer: Array<{
    containerId: string;
    containerName: string;
    cost: number;
  }>;
  ttsCost: number;
  ttsByContainer: Array<{
    containerId: string;
    containerName: string;
    cost: number;
  }>;
  // Month-to-date storage + linear projection to month end.
  projectedStorage: {
    monthToDate: { diskCost: number; backupCost: number; storageCost: number };
    projectedMonth: {
      diskCost: number;
      backupCost: number;
      storageCost: number;
    };
    daysElapsed: number;
    daysInMonth: number;
    now: string;
  };
  infraServices: Array<{
    serviceId: string;
    serviceName: string;
    serviceType: "database" | "app" | "container (deleted)" | "unknown";
    createdAt: string | null;
    cpuCost: number;
    memoryCost: number;
    networkCost: number;
    volumeCost: number;
    backupCost: number;
    rawCost: number;
  }>;
  // When the cached Railway infra-cost snapshot was last refreshed (driven
  // by the compute-sync endpoint). Null on a fresh DB before any sync.
  infraLastSyncedAt: string | null;
  // The 30-day window the snapshot represents. Independent of `period`,
  // which still controls the AI/compute aggregates above.
  infraSnapshotPeriodStart: string | null;
  infraSnapshotPeriodEnd: string | null;
  aiByModel: Array<{
    model: string;
    cost: number;
    inputTokens: number;
    outputTokens: number;
    requests: number;
  }>;
  aiByContainer: Array<{
    containerId: string | null;
    containerName: string;
    cost: number;
    inputTokens: number;
    outputTokens: number;
    requests: number;
  }>;
  computeByContainer: Array<{
    containerId: string | null;
    containerName: string;
    cpuMinutes: number;
    memoryGbMinutes: number;
    volumeGbHours: number;
    backupGbHours: number;
    diskCost: number;
    backupCost: number;
    storageCost: number;
    rawCost: number;
    markedUpCost: number;
  }>;
}

export interface AppRow {
  id: string;
  name: string;
  description: string | null;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminOrgBillingResponse {
  organizationId: string;
  creditBalance: number;
  markup: {
    /** null = inheriting global */
    override: number | null;
    global: number;
    effective: number;
  };
  consumption: {
    totalRaw: number;
    totalMarkedUp: number;
    requestCount: number;
  };
  /** Rolling-window activity for the per-period cards (24h / week / month). */
  windows: Array<{
    key: "24h" | "7d" | "30d";
    label: string;
    spend: {
      totalRaw: number;
      totalMarkedUp: number;
      requestCount: number;
    };
    creditsConsumed: number;
    creditsGranted: number;
  }>;
  recentConsumption: Array<{
    gatewayRequestId: string;
    containerId: string | null;
    rawCostUsd: number;
    markedUpCostUsd: number;
    markupPercent: number;
    createdAt: string;
  }>;
}

export interface AdminLedgerEntry {
  id: string;
  amount: number;
  balanceAfter: number;
  currency: string;
  type: string;
  description: string | null;
  containerId: string | null;
  createdAt: string;
}

export interface AdminLedgerPage {
  total: number;
  limit: number;
  offset: number;
  entries: AdminLedgerEntry[];
}

/** Admin view of an org's prepaid state: billing account + payments slice
 *  of the ledger with Stripe invoice links. account is null for orgs that
 *  have never touched a payment flow. */
export interface AdminOrgPrepaid {
  account: {
    provider: string;
    providerCustomerId: string | null;
    paymentMethodBrand: string | null;
    paymentMethodLast4: string | null;
    autoRecharge: {
      enabled: boolean;
      thresholdUsd: number | null;
      amountUsd: number | null;
      monthlyCapUsd: number | null;
      monthUsedUsd: number;
      consecutiveFailures: number;
      disabledReason: string | null;
      lastAttemptAt: string | null;
    };
  } | null;
  payments: {
    total: number;
    limit: number;
    offset: number;
    entries: Array<
      AdminLedgerEntry & {
        grantLabel: string | null;
        createdBy: string | null;
        invoiceUrl: string | null;
        invoicePdf: string | null;
      }
    >;
  };
}

// ── Prepaid credits (BOO-331) ──────────────────────────────────────

// The invoice "Bill to" block (GET/PUT /api/billing/details). name null =
// the org name, which the backend returns as orgName; address null = none on
// file. Mirrored onto the payment provider's customer on save.
export interface BillingAddress {
  line1: string;
  line2: string | null;
  city: string;
  state: string | null;
  postalCode: string;
  country: string; // ISO 3166-1 alpha-2
}

export interface BillingDetails {
  name: string | null;
  address: BillingAddress | null;
  orgName: string | null;
  // Who last saved these, for the change line under the summary.
  updatedAt: string | null;
  updatedBy: { name: string } | null;
  _permissions: Permissions;
}

export interface AutoRechargeConfig {
  enabled: boolean;
  thresholdUsd: number | null;
  amountUsd: number | null;
  monthlyCapUsd: number | null;
  monthUsedUsd: number;
  consecutiveFailures: number;
  disabledReason: string | null;
  paymentMethod: { brand: string; last4: string } | null;
  _permissions: Permissions;
}

// ── Spending limits (boo-backend docs/spend-limits.md §8) ─────────────

/** What a limit is on. `person_default` is the "Everyone (default)" row. */
export type SpendTarget =
  | { scope: "org" }
  | { scope: "boo"; containerId: string }
  | { scope: "group"; groupId: string }
  | { scope: "person"; personKey: string }
  | { scope: "person_default" };

export interface SpendPersonRef {
  /** `u:<userId>` for an org member, `g:<provider>:<workspace>:<providerUserId>` for a guest. */
  personKey: string;
  userId: string | null;
  name: string;
  /** Org role, set on people who can change limits. */
  role?: "owner" | "admin" | "billing_admin";
}

/** One period of one limit. capUsd null = no limit for that period. */
export interface SpendCell {
  capUsd: number | null;
  /** One-time top-ups granted for the current period. */
  extraUsd: number;
  spentUsd: number;
  resetsAt: string;
}

/** A one-time extra (a grant) on a limit for the current day or month. */
export interface SpendExtra {
  id: string;
  target: SpendTarget;
  targetKey: string;
  period: "daily" | "monthly";
  /** Org-local YYYY-MM-DD. */
  periodStart: string;
  amountUsd: number;
  grantedBy: SpendPersonRef;
  /** Set when it came from a chat request. */
  requestId: string | null;
  createdAt: string;
}

export interface SpendRow {
  target: SpendTarget;
  name: string;
  daily: SpendCell;
  monthly: SpendCell;
  /** Effective warning level. */
  warnPercent: number;
  /** The row's own level; null = on the org default. */
  warnPercentOwn: number | null;
  /** This day's and month's extras on this row; their sum is each cell's extraUsd. */
  extras: SpendExtra[];
}

export type SpendBooRow = SpendRow & { containerId: string; peopleCount: number };
export type SpendGroupRow = SpendRow & { groupId: string; members: SpendPersonRef[] };
export type SpendDefaultRow = SpendRow & { appliesTo: number };
export type SpendOverrideRow = SpendRow & {
  person: SpendPersonRef & { isGuest: boolean; guestLabel?: string };
};
export type SpendDirectoryEntry = SpendPersonRef & {
  email: string | null;
  groupId: string | null;
  isGuest: boolean;
};

export interface SpendLimitsPage {
  /** IANA zone every period resets in. */
  timezone: string;
  defaultWarnPercent: number;
  viewer: { orgRole: OrgRole; canEdit: boolean; canRaise: SpendPersonRef[] };
  org: SpendRow | null;
  boos: SpendBooRow[];
  groups: SpendGroupRow[];
  /** For a viewer who cannot edit and has no own limit, `default` carries the
   *  viewer's own spend (their line); for editors its spend is 0. Editors get
   *  it with no caps when none is set (older backends send null; see
   *  `defaultRowOf`). */
  people: { default: SpendDefaultRow | null; overrides: SpendOverrideRow[] };
  /** Everyone who can be given a limit or a group; present only when viewer.canEdit. */
  directory: SpendDirectoryEntry[] | null;
  /** Every current extra, including people on the default; present only when viewer.canEdit. */
  extras: SpendExtra[] | null;
  _permissions: { canManageSpendLimits: boolean };
}

/** A one-time extra on a limit for the current period. */
export interface SpendGrantInput {
  target: SpendTarget;
  period: "daily" | "monthly";
  amountUsd: number;
}

/** `PUT /api/billing/spend-limits/limit`: omitted = unchanged, null = remove. */
export interface SpendLimitInput {
  target: SpendTarget;
  dailyUsd?: number | null;
  monthlyUsd?: number | null;
  warnPercent?: number | null;
}

export interface SpendGroupInput {
  name: string;
  dailyUsd?: number | null;
  monthlyUsd?: number | null;
  warnPercent?: number | null;
}

export interface SpendPeopleLine {
  period: "daily" | "monthly";
  capUsd: number;
  extraUsd: number;
  spentUsd: number;
  source: "own" | "default";
}

/** `GET /api/containers/:id/spend-limits/people` (§8.2). */
export interface SpendPeopleResponse {
  data: { personKey: string; line: SpendPeopleLine | null }[];
  canChangeInBilling: boolean;
}

/** Org spend caps through the deprecated `/api/billing/spend-caps`. The new
 *  page uses it only to change the reset timezone, which §8.1 does not write. */
export interface SpendCapsResponse {
  caps: {
    dailyUsd: number | null;
    monthlyUsd: number | null;
    timezone: string;
  };
  status: {
    daily: { capUsd: number | null; spentUsd: number; withinCap: boolean };
    monthly: { capUsd: number | null; spentUsd: number; withinCap: boolean };
  };
}

/** Customer ledger page — AdminLedgerPage shape plus invoice links + _permissions. */
export interface TransactionsPage {
  total: number;
  limit: number;
  offset: number;
  entries: Array<
    AdminLedgerEntry & {
      grantLabel: string | null;
      invoiceUrl: string | null;
      invoicePdf: string | null;
    }
  >;
  _permissions: Permissions;
}

export interface MockSession {
  id: string;
  kind: "purchase" | "setup";
  amountUsd: number | null;
  status: "pending" | "completed" | "cancelled" | "expired";
}

export interface AdminGrantEntry {
  id: string;
  organizationId: string;
  amount: number;
  balanceAfter: number;
  type: string;
  description: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface AdminGrantsPage {
  total: number;
  limit: number;
  offset: number;
  entries: AdminGrantEntry[];
}

export interface AdminBillingConfigRow {
  key: string;
  value: string;
  description: string | null;
  updatedAt: string;
}

export interface AdminCronHealth {
  ok: boolean;
  crons: Array<{
    name: string;
    expectedWithinHours: number;
    lastSuccessAt: string | null;
    hoursSinceSuccess: number | null;
    ok: boolean;
  }>;
}

export interface BillingOverview {
  creditBalance: number;
  currency: string;
  currentMonth: {
    basePrice: number;
    ai: number;
    /** Compute + storage. */
    infrastructure: number;
    /** Search, sandbox, image, TTS, email, etc. 0 until external charging is on. */
    services: number;
    total: number;
    /** Per-service breakdown — present only for super admins. */
    detailed?: {
      aiCost: number;
      computeCost: number;
      storageCost: number;
      searchCost: number;
      sandboxCost: number;
      imageCost: number;
      ttsCost: number;
      backupCostUnbilled: number;
      backupCharged: boolean;
      externalCharged: boolean;
    };
  };
  _permissions: Permissions;
}

export interface CreditEntry {
  id: string;
  organizationId: string;
  amount: number;
  balanceAfter: number;
  currency: string;
  type: string;
  referenceType: string | null;
  referenceId: string | null;
  description: string | null;
  containerId: string | null;
  createdAt: string;
}

export interface CreditInfo {
  balance: number;
  currency: string;
  recentTransactions: CreditEntry[];
  _permissions: Permissions;
}

export interface CurrentUsageContainer {
  containerId: string;
  name: string;
  currency: string;
  basePrice: number;
  ai: number;
  /** Compute + storage. */
  infrastructure: number;
  /** Search, sandbox, image, TTS, etc. 0 until external charging is on. */
  services: number;
  total: number;
  /** Per-service breakdown — present only for super admins. */
  detailed?: {
    computeCost: number;
    storageCost: number;
    searchCost: number;
    sandboxCost: number;
    imageCost: number;
    ttsCost: number;
    backupCostUnbilled: number;
  };
}

export interface CurrentUsage {
  perContainer: CurrentUsageContainer[];
  currency: string;
  total: {
    basePrice: number;
    ai: number;
    infrastructure: number;
    services: number;
    total: number;
    /** Aggregate per-service breakdown — present only for super admins. */
    detailed?: {
      computeCost: number;
      storageCost: number;
      searchCost: number;
      sandboxCost: number;
      imageCost: number;
      ttsCost: number;
      backupCostUnbilled: number;
    };
  };
  // Timestamp of the latest compute_usage_snapshot row for this org. null when
  // no compute has been synced yet. The 6h cron means the displayed Compute
  // number is at minimum that stale; the frontend renders it next to the
  // number so users aren't misled.
  computeLastSyncedAt: string | null;
  _permissions: Permissions;
}

export interface DeploymentSnapshot {
  // The row's deploymentStatus, which the backend stores as free text.
  status: "running" | "creating" | "restarting" | "error" | "stopped" | (string & {});
  raw: string;
  errorMessage: string | null;
  // Setup conditions, for a Boo on a cluster (lib/setup-progress.ts).
  conditions?: import("./setup-progress").SetupConditions;
}

// Deployment posture: the product shape a Boo runs as. Declared at claim
// from the onboarding entry path, changeable from the General tab. A
// different axis from agentType (the image).
export type Posture = "teammate" | "chief-of-staff";
// Provisioning inputs the brain's setup interview confirms instead of asking.
// Chief-of-staff only; travel to the brain once and are not stored here.
export interface PostureSetup {
  timeZone?: string;
  briefingTime?: string;
}

// Dashboard roles inside one Boo. The org role runs the fleet; this decides
// what someone can see and change inside the Boo.
export type BooRole = "owner" | "admin" | "member";

// Mirrors boo-backend permissions/boo-policies.ts: one flag per Boo action.
export interface BooPermissions {
  canStatus: boolean;
  canView: boolean;
  canChat: boolean;
  canConfigure: boolean;
  canReadSecrets: boolean;
  canReadLogs: boolean;
  canViewReports: boolean;
  canViewMembers: boolean;
  canManageMembers: boolean;
  canManageAdmins: boolean;
  canLink: boolean;
  canAttachWorkspace: boolean;
  canReleaseWorkspace: boolean;
  canLifecycle: boolean;
  canViewSpend: boolean;
  canDelete: boolean;
  canTakeOwnership: boolean;
}

export interface BooPerson {
  userId: string;
  name: string | null;
  email: string | null;
}

export interface BooMember extends BooPerson {
  role: BooRole;
  addedAt: string;
}

export interface BooMembersResponse {
  data: BooMember[];
  ownerless: boolean;
  myRole: BooRole | null;
  _permissions: BooPermissions;
}

export interface BooMemberCandidate extends BooPerson {
  orgRole: string;
}

export type BooAccessEventKind =
  | "role_granted"
  | "role_changed"
  | "role_removed"
  | "ownership_taken"
  | "owner_assigned"
  | "deleted_by_org_owner"
  | "workspace_attached"
  | "workspace_released"
  | "link_requested"
  | "link_approved"
  | "link_declined"
  | "link_revoked"
  | "link_more_requested"
  | "link_more_approved"
  | "link_more_declined"
  | "link_grant_changed"
  | "link_removal_confirmed"
  | "link_expired"
  | "link_admins_asked"
  | "boo_handed_over"
  | "memories_archived"
  | "roles_changed"
  | "person_blocked"
  | "person_unblocked"
  | "view_all_spend_granted"
  | "view_all_spend_revoked"
  | "group_added"
  | "group_removed"
  | "groups_kept"
  | "invite_access_removed"
  | "handle_linked"
  | "handle_unlinked"
  | "super_admin_access";

export interface BooAccessEvent {
  id: string;
  kind: BooAccessEventKind;
  actor: BooPerson | null;
  target: BooPerson | null;
  role: string | null;
  previousRole: string | null;
  reason: string | null;
  details: Record<string, unknown> | null;
  // True while it still shows as a banner in the Boo.
  notice: boolean;
  createdAt: string;
}

export interface ContainerRow {
  id: string;
  name: string;
  description: string | null;
  organizationId: string;
  status: string;
  // creating (first setup), restarting, running, stopped or error.
  deploymentStatus: string;
  // Runs on the customer's own machine. Missing from a backend that predates it.
  selfHosted?: boolean;
  errorMessage: string | null;
  // The first time the Boo was running; null while it's being set up.
  // Missing from a backend that predates it.
  readyAt?: string | null;
  slackTeamId: string | null;
  agentType?: string;
  posture?: Posture;
  createdAt: string;
  updatedAt: string;
  taskCount?: number;
  lastActivityAt?: string | null;
  // Branded URL under boolab.io. Null until the brain container's custom
  // domain has been requested. Status walks pending → verifying → active
  // (or → error). Customers can still hit railwayServiceUrl throughout.
  customDomainFqdn?: string | null;
  customDomainStatus?: "pending" | "verifying" | "active" | "error" | null;
  customDomainError?: string | null;
  // The viewer's role in this Boo; null means they only see it in the
  // directory. Every org member sees every Boo's owners.
  myRole?: BooRole | null;
  owners?: BooPerson[];
  _permissions?: BooPermissions;
}

export interface OrgSubdomain {
  subdomain: string | null;
  rootDomain: string;
}

export interface AdminOrgSubdomainContainer {
  id: string;
  name: string;
  customDomainFqdn: string | null;
  customDomainStatus: "pending" | "verifying" | "active" | "error" | null;
  customDomainError: string | null;
}

export interface AdminOrgSubdomain {
  subdomain: string | null;
  containers: AdminOrgSubdomainContainer[];
}

export interface AdminSubdomainsListRow {
  organizationId: string;
  subdomain: string;
  updatedAt: string;
}

export interface AdminSubdomainPatchResult {
  subdomain: string;
  reprovisioning: { id: string; name: string }[];
}

export interface UsageTotals {
  totalCost: number;
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  count: number;
}

export interface ContainerUsage {
  total: UsageTotals;
  last7d: UsageTotals;
  last30d: UsageTotals;
}

export interface ContainerCurrentMonthBilling {
  containerId: string;
  currency: string;
  basePrice: number;
  ai: number;
  /** Compute + storage. */
  infrastructure: number;
  /** Search, sandbox, image, TTS, etc. 0 until external charging is on. */
  services: number;
  total: number;
  /** Per-service breakdown — present only for super admins. */
  detailed?: {
    computeCost: number;
    storageCost: number;
    searchCost: number;
    sandboxCost: number;
    imageCost: number;
    ttsCost: number;
    backupCostUnbilled: number;
  };
  /** MAX(compute_usage_snapshot.createdAt) for the org. */
  computeLastSyncedAt: string | null;
}

/** Daily spend for one Boo, ledger-derived — the single money source for the
 * Usage tab. Window totals are client-side sums of these rows so daily,
 * weekly, and monthly figures always tally. */
export interface ContainerUsageByDay {
  since: string;
  monthStart: string;
  days: Array<{
    day: string;
    total: number;
    ai: number;
    base: number;
    infrastructure: number;
    services: number;
    tokens: number;
    turns: number;
  }>;
}

// ── Spend attribution (the Boo's own usage ledger, proxied via /usage-report) ──

export const USAGE_REPORT_PERIODS = ["24h", "7d", "30d", "all"] as const;
export type UsageReportPeriod = (typeof USAGE_REPORT_PERIODS)[number];

/** Picker labels for the one window the Boo Usage tab runs on: the brain's
 * ledger periods, which the ledger-side reads are cut to as well. */
export const USAGE_REPORT_PERIOD_OPTIONS: Array<{ key: UsageReportPeriod; label: string }> = [
  { key: "24h", label: "Last 24 hours" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "all", label: "All time" },
];

export const USAGE_REPORT_GROUPS = [
  "user",
  "channel",
  "thread",
  "purpose",
  "heartbeat-task",
  "workflow-run",
  "subagent-kind",
  "skill",
] as const;
export type UsageReportGroup = (typeof USAGE_REPORT_GROUPS)[number];

/** Model usage at the org's billed rate (the brain's measured cost with the
 * org's AI markup applied backend-side). Infrastructure and add-on services
 * are not attributed to people or channels, so the breakdown totals less than
 * the ledger-derived total-spend card. */
export interface UsageReportTotals {
  costUsd: number;
  entries: number;
  /** Distinct conversations the entries span. */
  threads: number;
}

export interface UsageReportRow {
  key: string;
  /** Human name when the key is an opaque id (workflow runs). */
  label?: string;
  costUsd: number;
  entries: number;
  /** Epoch ms of the row's most recent ledger entry. */
  lastTs: number;
  /** Resolved for groupBy=user rows only. */
  displayName?: string;
  provider?: string;
  providerUserId?: string;
}

export const USAGE_REPORT_KINDS = [
  "turn",
  "subagent",
  "workflow-step",
  "intent",
  "approval",
  "compaction",
  "retrospect",
  "conversation-index",
  "companion",
] as const;
export type UsageReportKind = (typeof USAGE_REPORT_KINDS)[number];

/** Mirrors the Boo's usage filter sentinel: as the `userId` filter it scopes to
 * entries with no person, as the `channelId` filter to entries with no
 * channel. Boos that predate it match nothing (an empty scoped view). */
export const USAGE_FILTER_NONE = "__none__";

export interface UsageReportFilter {
  channelId?: string;
  threadId?: string;
  userId?: string;
  purpose?: string;
  kind?: UsageReportKind;
}

export interface UsageReportTotalsResponse {
  period: UsageReportPeriod;
  totals: UsageReportTotals;
}

export interface UsageReportSummaryResponse {
  period: UsageReportPeriod;
  groupBy: UsageReportGroup;
  totals: UsageReportTotals;
  rows: UsageReportRow[];
  /** Spend with no value for the dimension (system-started work for `user`,
   * channel-less threads for `channel`). Render as its own labeled bucket —
   * never derive a residual from totals - rows. */
  unattributed: UsageReportTotals;
}

/** One time bucket of the spend series; `key` is the split-dimension value,
 * null for the unattributed remainder (and for every point when unsplit). */
export interface UsageSeriesPoint {
  bucketTs: number;
  key: string | null;
  costUsd: number;
  entries: number;
}

export interface UsageSeriesResponse {
  period: UsageReportPeriod;
  bucket: "hour" | "day";
  splitBy?: Exclude<UsageReportGroup, "skill">;
  points: UsageSeriesPoint[];
}

/** One conversation's rollup: cost and participants, nothing about what
 * happened inside it. */
export interface UsageSession {
  threadId: string;
  provider: string;
  channelId?: string;
  heartbeatTaskId?: string;
  participants: Array<{ userId: string; displayName?: string }>;
  /** Deep link into the platform's own UI (Slack permalink); absent when the
   * platform can't provide one. */
  link?: string;
  costUsd: number;
  entries: number;
  /** Completed turns — for scheduled tasks, how many runs the rollup spans. */
  runs: number;
  firstTs: number;
  lastTs: number;
}

export interface UsageSessionsResponse {
  period: UsageReportPeriod;
  sessions: UsageSession[];
  /** Cursor pair for the next-older page; absent when this page is short.
   * Both halves must be echoed back — the thread id breaks same-millisecond
   * ties so pages never lose rows. */
  nextBeforeTs?: number;
  nextBeforeThread?: string;
}

export interface ActivityWindow {
  callCount: number;
  lastActivityAt: string | null;
}

/** Org-wide ledger-derived daily spend rows for the Usage page cost view.
 * containerId null = org-level charges not tied to a single Boo. */
export interface OrgSpendByDay {
  days: Array<{
    day: string;
    containerId: string | null;
    total: number;
    ai: number;
    base: number;
    infrastructure: number;
    services: number;
  }>;
  containers: Array<{ id: string; name: string }>;
  _permissions: Permissions;
}

export interface OrgUsage {
  organization: {
    total: ActivityWindow;
    last7d: ActivityWindow;
    last30d: ActivityWindow;
  };
  perContainer: Array<{
    containerId: string;
    name: string;
    status: string;
    deploymentStatus: string;
    agentType: string;
    callCount: number;
    lastActivityAt: string | null;
  }>;
  _permissions: Permissions;
}

export interface SlackStatus {
  connected: boolean;
  teamId: string | null;
  teamName: string | null;
  botName: string | null;
  botUserId: string | null;
  installerDmChannelId: string | null;
  agentOffline: boolean;
  appCreated: boolean;
  _permissions: Permissions;
}

export interface BooSlackStatus {
  installed: boolean;
  // Connected to the org, attached or not. Older backends omit it.
  workspaceConnected?: boolean;
  teamId: string | null;
  teamName: string | null;
  containerId: string | null;
  containerName: string | null;
  botUserId: string | null;
  installerDmChannelId: string | null;
  _permissions: Permissions;
}

export interface SlackWorkspaceStatus {
  connected: boolean;
  teamId: string | null;
  teamName: string | null;
  _permissions: Permissions;
}

export interface BooTeamsStatus {
  installed: boolean;
  // Connected to the org, attached or not. Older backends omit it.
  workspaceConnected?: boolean;
  tenantId: string | null;
  tenantName: string | null;
  containerId: string | null;
  containerName: string | null;
  botId: string | null;
  installerConversationId: string | null;
  _permissions: Permissions;
}

export interface BooDiscordStatus {
  installed: boolean;
  // Connected to the org, attached or not. Older backends omit it.
  workspaceConnected?: boolean;
  guildId: string | null;
  guildName: string | null;
  containerId: string | null;
  containerName: string | null;
  installerUserId: string | null;
  _permissions: Permissions;
}

export interface BooGchatStatus {
  installed: boolean;
  // Connected to the org, attached or not. Older backends omit it.
  workspaceConnected?: boolean;
  customerId: string | null;
  customerName: string | null;
  containerId: string | null;
  containerName: string | null;
  _permissions: Permissions;
}

export interface GchatCustomStatus {
  connected: boolean;
  agentOffline: boolean;
  customerId: string | null;
  customerName: string | null;
  // URL the customer pastes into Google Cloud Console's Chat API
  // configuration as the HTTP endpoint URL. Picks a custom Railway domain
  // when one is attached to the container's service; falls back to the
  // .up.railway.app default. Null when the container isn't yet provisioned.
  eventsUrl: string | null;
  _permissions: Permissions;
}

export interface GchatCustomUserOauthStatus {
  clientConfigured: boolean;
  connected: boolean;
  agentOffline: boolean;
  // Brain is running but doesn't have the user-OAuth endpoints yet
  // (its image predates the feature). Distinct from agentOffline so the
  // UI can show a "not ready yet" message instead of a generic outage.
  featureUnavailable: boolean;
  providerUserId?: string | null;
  scope?: string | null;
  redirectUri?: string | null;
  // URL the admin clicks to start the Google consent flow. The brain 302s
  // them to Google with a state-signed authorize request. Null when the
  // OAuth client hasn't been configured yet (clientConfigured: false).
  installUrl: string | null;
  _permissions: Permissions;
}

export interface GchatWorkspaceAdminState {
  connected: boolean;
  connectedAs: string | null;
  lastRefreshedAt: string | null;
  grantedScopes: ReadonlyArray<string>;
  status: "healthy" | "needs_reauth" | "disconnected";
  redirectUri: string | null;
  clientConfigured: boolean;
  agentOffline: boolean;
  featureUnavailable: boolean;
  // Base URL the dashboard's Authorize button appends ?scopes=<csv> to.
  // Null while the OAuth client isn't configured or the brain is unreachable.
  installUrlBase: string | null;
  _permissions: Permissions;
}

export interface GchatWorkspaceVisibilityProbe {
  count: number;
  examples: ReadonlyArray<string>;
  capped: boolean;
}

export interface GchatWorkspaceVisibility {
  connected: boolean;
  items: Record<string, GchatWorkspaceVisibilityProbe>;
  errors: Record<string, string>;
}

// --- Default vault (boo-brain) -------------------------------------------
// The brain's default vault stores secrets as mount → path → { field: value }.
// All calls proxy through the backend (never the container directly), which
// forwards the brain's status verbatim so 404 not_found / 400 invalid_path /
// 409 stay distinguishable. See VAULT_PROXY_SPEC.md.

export interface VaultSecretMetadata {
  createdAtMillis: number;
  updatedAtMillis: number;
}

export interface VaultSecret {
  data: Record<string, string>; // field -> plaintext value
  metadata: VaultSecretMetadata;
}

export interface VaultListResponse {
  keys: string[]; // full secret paths under the queried mount/prefix
}

// --- Storage protection (boo-brain at-rest encryption) ---------------------
// The backend reads the brain's GET /atrest/state with its own token. The
// brain answers disabled | uninitialized | sealed | active | degraded with the
// primary key provider's type ("password" when a person must unlock it); the
// backend answers { status: "unknown", primaryType: null } when the brain
// can't be read (old brain, offline, timeout, not running).
export type AtrestStatus =
  | "disabled"
  | "uninitialized"
  | "sealed"
  | "active"
  | "degraded"
  | "unknown";

export interface AtrestState {
  status: AtrestStatus;
  primaryType?: string | null;
}

// A one-hour certificate for the brain's storage console, already placed in
// the console URL: https://<brain host>/atrest#oc=<certificate>. `exp` is in
// unix seconds.
export interface OperatorCertificate {
  url: string;
  exp?: number;
}

// --- Integrations Hub (Apps grid) -----------------------------------------
// Connected accounts and their credentials live in the Boo Integrations Hub
// (external service). The browser never talks to the hub: the catalog is read
// through our backend, and a connect is a full-page redirect through
// POST /connect-links → the hub's hosted connect page → back to the dashboard
// with ?integrations=connected|cancelled|error. Our backend owns each account's
// scope (private to one Boo, or shared with the workspace) and its *bindings*:
// where it may be used and which hub tools it allows. See boo-backend
// src/routes/integrations.ts and src/routes/connect-public.ts.

// One app in the hub catalog.
export interface AppInfo {
  slug: string;
  name: string;
  authType: "oauth" | "keys" | "none";
  description?: string;
  logoUrl?: string;
  categories: string[];
  /** Hub curation: floated to the top of the catalog. */
  promoted?: boolean;
  /** "app": listed for discovery only; boo-backend connects it with its own
   *  flow (Microsoft 365), and the hub answers `unsupported` for it. */
  connectedBy?: "hub" | "app";
}

export interface AppsPage {
  apps: AppInfo[];
  /** Pass back to fetch the next page; absent on the last page. */
  nextCursor?: string;
}

export interface CategoryInfo {
  slug: string;
  name: string;
  /** null = top-level; nest children under their parent for a tree. */
  parentSlug: string | null;
  sortRank: number;
}

// How a connect redirect came back (the `integrations` query param).
export type ConnectOutcome = "connected" | "cancelled" | "error";

// Per-tool policy. "on" = run freely, "ask" = confirm with the user first
// (enforcement lands later; stored + surfaced now), "off" = refused.
export type ToolState = "on" | "ask" | "off";
export interface ToolAccess {
  default: ToolState; // covers tools not listed, including ones added later
  overrides: Record<string, ToolState>; // per-tool exceptions
}

export interface IntegrationBinding {
  id: string;
  hubAccountId: string;
  appSlug: string; // hub app slug, e.g. "gmail" — open string, never an enum
  containerId: string | null; // null = org-wide (usable by every Boo)
  label: string | null;
  // What this account is FOR ("support inbox") — shown to Boos so they can
  // tell same-app accounts apart. Backend caps at 80 chars.
  purpose: string | null;
  toolAccess: ToolAccess;
  createdAtMillis: number;
}

// Who may see and use an account. "boo": private to its home Boo — only that
// Boo's members see it, only that Boo may be bound. "workspace": connected at
// org level or shared by the home Boo's admins; any Boo may be bound.
export type HubAccountScope = "boo" | "workspace";

export interface HubAccountWithBindings {
  id: string; // hub account id (acc_…)
  app: string; // hub app slug
  appName: string | null; // catalog display name ("Google Sheets"); null if unresolved
  logoUrl: string | null; // catalog logo; null → UI falls back to a monogram
  label: string; // "you@work.com"
  healthy: boolean;
  createdAt: string;
  scope: HubAccountScope;
  // The Boo it was connected from; null when connected at org level.
  homeContainerId: string | null;
  // The viewer may add, change and remove EVERY binding of this account, the
  // org-wide one included. Private: the home Boo's admins. Workspace: org
  // owners/admins only. (Configure on a Boo still lets the viewer bind a
  // workspace account to that Boo and manage that one binding.) The
  // list-level `_permissions.canManage` is the org-level gate only.
  canManage: boolean;
  // The viewer may share it with the workspace or take it private again
  // (configure on the home Boo). Always false when homeContainerId is null.
  canShare: boolean;
  // The viewer may disconnect it at the hub. Private: home configure.
  // Workspace: org owner/admin or home configure.
  canDisconnect: boolean;
  bindings: IntegrationBinding[]; // empty → connected but usable nowhere yet
}

// One tool an app exposes, e.g. "google_sheets:add-single-row".
export interface HubTool {
  id: string;
  app: string;
  name: string;
  description?: string;
  // The binding default never turns this tool on (an app's raw-request): it is
  // "off" unless an override sets it, so keep its override even when it equals
  // the default.
  requiresExplicitEnable?: boolean;
  // Set on the two API access rows: "full" = the app's raw-request, "read" =
  // the dashboard-only `{app}:raw-request-read`. Absent on ready-made actions
  // and from older backends.
  apiAccess?: "read" | "full";
}

// ── Connectors (small apps Boo writes for one service) ──────
// Per Boo: every route is under /api/containers/:id/connectors (reads need
// view on the Boo, writes configure). A connector is a git repo
// (connector.yaml + one handler per action) that runs only at a commit a
// person approved. Boo-hosted ones are reviewed here; code-host ones in a pull
// request on GitHub or GitLab.

// Declared intent per action; sets the defaults on first approval (read on,
// write ask, destructive off). Actions added by a later version start off.
export type ConnectorRisk = "read" | "write" | "destructive";
// live: a version is approved and running. disabled: a version is live but
// its connection was removed. in_review: nothing live, the newest version
// passed its checks. draft: anything else.
export type ConnectorStatus = "draft" | "in_review" | "live" | "disabled";
export type ConnectorSourceKind = "boo" | "github" | "gitlab";
// A registered commit: checking → failed | ready → approved, or superseded
// by a newer commit.
export type ConnectorVersionStatus = "checking" | "failed" | "ready" | "approved" | "superseded";

export interface ConnectorSource {
  kind: ConnectorSourceKind;
  // Code host: the repository's web page.
  repoUrl?: string | null;
}

export interface ConnectorCheck {
  name: string;
  ok: boolean;
  message?: string | null;
}

// What `verify` returned: who the connection signs in as, and named checks.
export interface ConnectorVerifyResult {
  account?: string | null;
  checks: ConnectorCheck[];
}

export interface ConnectorSummary {
  slug: string;
  name: string;
  status: ConnectorStatus;
  source: ConnectorSource;
  runningCommit?: string | null;
  pendingCommit?: string | null;
  // The connection's label from the last passing verify ("jane@acme.com").
  account?: string | null;
}

export interface ConnectorAction {
  name: string;
  description: string;
  risk: ConnectorRisk;
  state: ToolState;
}

export interface ConnectorVariable {
  name: string;
  label: string;
  secret: boolean;
  // JS regex source the whole value must match.
  pattern?: string | null;
  // A value is stored. Secret values are never returned.
  set: boolean;
  // Non-secret only.
  value?: string | null;
}

// A code-host version's pull/merge request, for information: on GitHub and
// GitLab the default branch is what runs, so nobody approves here.
export interface ConnectorCodeHostApproval {
  kind?: "github" | "gitlab";
  // null while state is "none".
  prUrl: string | null;
  prNumber?: number | null;
  // none: no pull/merge request has this commit as its head.
  state: "open" | "merged" | "closed" | "none";
  // Who approved on the host. Shown only; approvals gate nothing.
  approvedBy: string[];
  // The request's head is this commit.
  headMatches: boolean;
  // Detail GET only: why this request isn't live.
  code?: ConnectorHostCheckCode | null;
  reason?: string | null;
}

export type ConnectorHostCheckCode =
  | "not_merged"
  | "closed"
  | "boo_moved_main"
  | "mover_unknown"
  | "checks_failed"
  | "no_connector"
  | "not_ready";

// Who put a commit on the default branch, from the host's own records.
export interface ConnectorMovedBy {
  login: string;
  via: "merge" | "push";
  prNumber?: number | null;
  prUrl?: string | null;
}

// A change Boo's own account put on the default branch (its push, or a
// request it merged) that went live with a person's later move.
export interface ConnectorBooMove {
  commit: string;
  // merge_queue: a merge queue merged it; who queued it isn't known.
  via: "merge" | "push" | "merge_queue";
  prNumber?: number | null;
}

// Code host: present when the host's records of the default branch ended
// before the running version landed, so moves before `at` went unchecked.
export interface ConnectorHistoryFrom {
  commit: string | null;
  at: string;
}

// Code host: what came of the default branch's current head. null until the
// first check.
export interface ConnectorMain {
  branch: string;
  commit: string;
  live: boolean;
  code?: "boo_moved_main" | "mover_unknown" | "checks_failed" | "no_connector" | null;
  reason?: string | null;
  movedBy?: ConnectorMovedBy | null;
  // Oldest first; absent when there are none.
  includesBooMoves?: ConnectorBooMove[] | null;
  historyFrom?: ConnectorHistoryFrom | null;
  checkedAt: string;
}

export interface ConnectorDetail {
  slug: string;
  name: string;
  status: ConnectorStatus;
  source: ConnectorSource;
  running: {
    commit: string;
    approvedBy?: string | null;
    approvedAt?: string | null;
    movedBy?: ConnectorMovedBy | null;
    includesBooMoves?: ConnectorBooMove[] | null;
    historyFrom?: ConnectorHistoryFrom | null;
  } | null;
  // The newest registered version that isn't live.
  pending: {
    commit: string;
    status: "checking" | "failed" | "ready";
    checks: ConnectorCheck[];
    approval?: ({ kind: "dashboard" } | ({ kind: "github" | "gitlab" } & ConnectorCodeHostApproval)) | null;
  } | null;
  actions: ConnectorAction[];
  variables: ConnectorVariable[];
  connection: { account?: string | null; checkedAt?: string | null; ok?: boolean | null; checks?: ConnectorCheck[] | null } | null;
  // Code host only: the default branch, which is what runs.
  main?: ConnectorMain | null;
  // configure: action states, approve, rollback, test, push access.
  // canConnect: may fill in the connection (configure, or anyone in the org
  // when the org lets everyone set up connectors). Absent: same as configure.
  _permissions: { canConfigure: boolean; canConnect?: boolean };
}

export interface ConnectorFileChange {
  path: string;
  status: "added" | "modified" | "removed" | "unchanged";
  // binary and too_large files carry size and never before/after text.
  kind?: "text" | "binary" | "too_large";
  size?: number | null;
  before?: string | null;
  after?: string | null;
  // A changed line mentions a secret variable.
  touchesSecret: boolean;
}

// One action as a version declares it; input/output are the YAML type strings.
export interface ConnectorActionDecl {
  name: string;
  description: string;
  risk: ConnectorRisk;
  input?: unknown;
  output?: unknown;
}

export interface ConnectorVariableDecl {
  name: string;
  label: string;
  secret?: boolean;
  pattern?: string | null;
}

export interface ConnectorComment {
  id: string;
  // null userId: Boo's own reply.
  author: { userId?: string | null; name: string };
  body: string;
  path?: string | null;
  line?: number | null;
  at: string;
}

// A version's declared variables, no values: which ones are secret.
export interface ConnectorDeclaredVariable {
  name: string;
  label: string;
  secret: boolean;
}

export interface ConnectorReview {
  commit: string;
  status: ConnectorVersionStatus;
  // Against the running version; every file for a first version.
  files: ConnectorFileChange[];
  actions: {
    added: ConnectorActionDecl[];
    changed: { name: string; before: ConnectorActionDecl; after: ConnectorActionDecl }[];
    removed: ConnectorActionDecl[];
  };
  variables: {
    added: ConnectorVariableDecl[];
    changed: { name: string; before: ConnectorVariableDecl; after: ConnectorVariableDecl }[];
    removed: ConnectorVariableDecl[];
  };
  // This version's and the compared version's declarations, for marking the
  // lines that read a secret on each side. Absent from older backends.
  versionVariables?: ConnectorDeclaredVariable[];
  baseVariables?: ConnectorDeclaredVariable[];
  verify: { description: string; note: string };
  // The platform's build: format, handlers, variables, imports, types, bundle, tests.
  checks: ConnectorCheck[];
  comments: ConnectorComment[];
  // Boo-hosted: who approved here. Code host: the pull request's review.
  approval:
    | { approvedBy?: { userId: string; name: string } | null; approvedAt?: string | null }
    | ConnectorCodeHostApproval
    | null;
  canApprove: boolean;
}

export interface ConnectorVersion {
  commit: string;
  // The commit message's first line.
  summary: string;
  // The commit author, or the Boo.
  author: string;
  // Boo-hosted: who approved. Code host: the login that put it on main.
  approver: string;
  approvedAt: string;
  // Code host: how it reached main, when the backend says.
  movedBy?: ConnectorMovedBy | null;
  includesBooMoves?: ConnectorBooMove[] | null;
  historyFrom?: ConnectorHistoryFrom | null;
  current: boolean;
}

export type ConnectorActivityKind = "call" | "code" | "variable" | "setting" | "approval" | "connection";

export type ConnectorActor =
  | { type: "user"; userId: string | null; name: string | null }
  | { type: "boo" }
  // Someone who approved an ask-first run in chat.
  | { type: "person"; displayName: string }
  // Someone who approved on GitHub or GitLab (their login there).
  | { type: "host"; login: string; host?: "github" | "gitlab" }
  // Whoever filled in the one-time link Boo sent (named as Boo sent it).
  | { type: "link"; sentTo?: string | null; mintedBy?: "boo" };

export interface ConnectorActivity {
  id: string;
  at: string;
  kind: ConnectorActivityKind;
  actor: ConnectorActor;
  action?: string | null;
  outcome?: string | null;
  // Calls, when the backend reports it.
  durationMs?: number | null;
  detail: string;
}

export interface ConnectorCloneToken {
  url: string;
  token: string;
  // Pushes land here for review; main moves only on approval.
  branch: string;
  expiresAt: string;
}

// One file of a version, read-only, as registered. content is null unless
// kind is "text" (binary, or over 256 KB).
export interface ConnectorFile {
  path: string;
  content: string | null;
  size: number;
  kind: "text" | "binary" | "too_large";
}

// The connection form alone, for someone allowed to set up connectors who
// has no role in the Boo. Nothing else of the Boo comes with it.
export interface ConnectorSetupView {
  slug: string;
  name: string;
  booName: string;
  status: ConnectorStatus;
  // From the running version, or the pending one while nothing is live.
  variables: ConnectorVariable[];
  verify: { description: string; note?: string | null } | null;
  connection: ConnectorDetail["connection"];
}

// The connection form behind a one-time link Boo sent one person (no sign-in;
// the org lets everyone set up connectors). 410 link_expired once used, past
// expiresAt, or the org switched to admins only; 404 unknown; 429 rate_limited;
// 409 link_busy on a save while another save through the link is running.
export interface ConnectorLinkView {
  name: string;
  booName: string;
  // No values, not even non-secret ones: only whether each is set. A field
  // left empty on save keeps what's stored.
  variables: ConnectorVariable[];
  verify: { description: string } | null;
  expiresAt: string;
}

// Who may fill in a connector's connection: anyone signed in to the org, or
// only the Boo's admins.
export type ConnectorSetupPolicy = "everyone" | "admins";

export interface OrgSettings {
  connectorSetup: ConnectorSetupPolicy;
  updatedAt: string | null;
  updatedBy: { userId: string; name: string } | null;
  // Newest first, at most 50.
  history: { at: string; actor: { userId: string; name: string }; from: ConnectorSetupPolicy; to: ConnectorSetupPolicy }[];
  // Owners and admins.
  _permissions: { canEdit: boolean };
}

// PUT /variables: saved (verify passed) or rejected with the reason.
export type ConnectorSaveResult =
  | ({ saved: true } & ConnectorVerifyResult)
  | ({ saved: false; code: "verify_failed" } & ConnectorVerifyResult)
  | { saved: false; code: "invalid_variables"; errors: Record<string, string> };

// ── Bundles (installable add-ons that run on a brain) ──────
// Per-brain: every route is scoped to the brain's container id. Mutations
// return 202 and the real work runs in the background: poll list/config
// until a terminal status.

// not_installed → pending → installing → installed
//             … or installing → error (read stage + error)
// uninstall: installed → uninstalling → uninstalled
// upgrade: installed → upgrading → installed (a failure lands in `upgrade`)
export type BundleInstallStatus =
  | "not_installed"
  | "pending"
  | "installing"
  | "installed"
  | "uninstalling"
  | "uninstalled"
  | "upgrading"
  | "error";

export interface BundleUpgradeState {
  available: boolean;
  installedVersion: string | null;
  // The version an in-flight or failed upgrade targets, snapshotted at click.
  // The brain advances installedVersion when an upgrade starts, so after a
  // failure installedVersion already equals the target.
  target: string | null;
  stage: string | null; // last failure, while the upgrade is still available
  error: string | null;
  unconfirmed: boolean; // the call timed out and the brain hasn't finished yet
  conflicts: string[]; // customized areas Boo is reconciling after the last upgrade
}

// What a Boo that reconciles its own bundles reports for one of them. The
// backend passes it through as `install.reconcile`.
export type BundlePhase = "converged" | "progressing" | "waiting" | "retrying" | "failed" | "degraded";

export interface BundleReconcileStatus {
  id: string;
  desired: "present" | "absent" | "untracked";
  phase: BundlePhase;
  installedVersion?: string;
  targetVersion?: string; // the version this Boo's image ships
  url?: string;
  platform?: DeployPlatform;
  domainAttached?: boolean;
  lastError?: { stage: string; reason: string; kind: "transient" | "permanent" | "waiting"; at: string };
  attempts: number;
  nextAttemptAt?: string; // ISO, while retrying
  rerunSeen: number; // the last Retry/Repair counter the Boo acted on
  upgradeConflicts?: string[];
  updatedAt: string;
}

export interface BundleInstallState {
  status: BundleInstallStatus;
  url: string | null; // set once installed
  stage: string | null; // failure stage when status === "error"
  error: string | null; // failure reason when status === "error"
  // App bundles: false = still on its Railway service domain (installed
  // before the Boo's domain existed); null = not applicable / unknown.
  domainAttached: boolean | null;
  // True while an attach is running for this app. Absent from older backends.
  domainAttaching?: boolean;
  domainError: string | null; // last failed domain attach, if any
  platform: string | null; // "railway" | "gcp" for app bundles; null otherwise
  // Where the app's source lives in GitHub ("owner/name" or a URL), when it does.
  repository: string | null;
  // Last failed re-key of an installed bundle; the install itself still works.
  inputsError: string | null;
  // Required input keys the last install or upgrade left blank.
  missingInputs: string[];
  upgrade: BundleUpgradeState;
  updatedAt: string | null; // null if never touched
  // The Boo's own status for this bundle; null unless the list says
  // `reconciler: 1`. Absent from older backends.
  reconcile?: BundleReconcileStatus | null;
  // Retry/Repair presses; the Boo has acted on them once
  // `reconcile.rerunSeen` reaches this. Absent from older backends.
  rerun?: number;
}

// `reconciler: 1`: this Boo installs, upgrades and repairs its bundles itself.
// Updates are automatic and Retry/Repair ask it to run a bundle again.
export type BundleListResponse = ListResponse<BundleCatalogEntry> & { reconciler?: 1 };

// A value the user supplies in the install form. Inputs the backend mints
// never appear here.
export interface BundleInput {
  key: string;
  name: string;
  description: string;
  secret: boolean;
  required: boolean;
  default?: string; // stored by the brain when an optional input is left out
  example?: string; // the field placeholder
}

export type DeployPlatform = "railway" | "gcp";

// Copy comes from the backend catalog: render verbatim (BOO-198).
export interface BundleCatalogEntry {
  id: string;
  name: string;
  tagline: string; // one line; the backend derives it for older brains
  description: string;
  category?: string; // the brain's list, open-ended; absent from older brains
  kind: "app" | "skill"; // app = runs at its own URL
  // Built into the Boo (from its manifest): a capability under Advanced, not
  // an App store item. Absent from older brains, which keep the plain store.
  core?: boolean;
  // Bundle ids that must be on the Boo for this one to work (Chief of Staff
  // needs Artifacts). Absent from older brains.
  requires?: string[];
  // How a built-in capability turns off, from its manifest.
  turnOff?: BundleTurnOff;
  version: string; // semver "x.y.z" the brain image ships
  slug?: string;
  // Deploy platforms this app supports; length > 1 means the install offers a
  // platform choice. Absent for a skill-only bundle.
  platforms?: DeployPlatform[];
  skills: string[];
  // Square app-tile renditions (src = absolute https URL, size = pixel width).
  icons: { src: string; size: number }[];
  inputs: BundleInput[];
  install: BundleInstallState;
}

export interface BundleTurnOff {
  allowed?: boolean; // false = always on; default true
  loses?: string; // what the Boo can no longer do, in the customer's words
  data?: "kept" | "deleted"; // what happens to what it stored
}

export interface BundleInstallOptions {
  platform?: DeployPlatform;
  source?: BundleSource;
  repository?: string;
  // The code host connection the source goes to; the Boo's default when absent.
  connectionId?: string;
  inputs?: Record<string, string>;
}

// pending → configuring → configured … or configuring → error
export type BundleConfigStatus =
  "pending" | "configuring" | "configured" | "error";

export type BundleRailwayTokenCheck =
  | { status: "ok"; projectId: string; environmentId: string }
  | { status: "missing" }
  | { status: "failing"; reason: string; httpStatus?: number }
  | { status: "wrong_project"; projectId: string; environmentId: string }
  | { status: "unsupported" }
  | { status: "unreachable"; reason: string };

export interface BundleRailwayTokenReport {
  projectId: string | null;
  brain: BundleRailwayTokenCheck;
  tokens: { name: string; createdAt: string }[] | null;
  tokensError?: string;
}

export interface BundleRailwayTokenRotation {
  minted: string;
  revoked: string[];
  revokeFailed: string[];
  check: BundleRailwayTokenReport | null;
}

export interface BundleConfig {
  status: BundleConfigStatus;
  domain: string | null; // the bundle domain, null until allocated
  error: string | null; // reason when status === "error"
  updatedAt: string | null;
}

export type Permissions = Record<string, boolean> | null;

export interface ListResponse<T> {
  data: T[];
  _permissions: Permissions;
}

export type ChannelPlatform =
  | "slack"
  | "gchat"
  | "teams"
  | "discord"
  | "dashboard_chat";

/**
 * Responding mode (channel-management v2 brains).
 * always = reply to every message · mentions = reply when addressed (DMs
 * always answer) · posts_only = can post here but never replies to inbound,
 * and sends no not-available reply · never = read silently, decline with the
 * not-available reply if @-mentioned.
 */
export type ChannelMode = "always" | "mentions" | "posts_only" | "never";

/** Surface vocabulary for per-surface defaults. `channel` = privacy public or unknown. */
export type ChannelSurfaceType =
  "dm" | "group_dm" | "channel" | "private_channel";

export interface ChannelRow {
  platform: ChannelPlatform;
  channelOrSpaceId: string;
  workspaceOrHostId: string | null;
  // Loose on purpose: v2 brains emit dm/group_dm/channel/private_channel, but
  // rows migrated from v1 keep their old strings (public_channel, space, …).
  surfaceType: string;
  displayName: string | null;
  /** Absent when the Boo predates channel-management v2. */
  mode?: ChannelMode;
  /** true = only explicitly allowed people get answers here. Absent = open. */
  allowlistOnly?: boolean;
  /** Derived for the pre-mode contract: mode !== "never". */
  responding: boolean;
  presence: "active" | "removed";
  firstSeenAt: string;
  lastMessageAt: string | null;
  lastToggledAt: string | null;
}

export interface ChannelUserRow {
  platform: ChannelPlatform;
  providerUserId: string;
  displayName: string | null;
  blocked: boolean;
  /**
   * Per-channel overrides keyed by channelOrSpaceId: "block" = quiet to them
   * there even though the channel is open, "allow" = answered there even when
   * the channel is allowlist-only. An explicit override always beats the
   * channel default. Absent when none (or on older Boos).
   */
  channelOverrides?: Record<string, "allow" | "block">;
  /** Whether this person may see all spend. Absent on Boos that predate the role. */
  viewAllSpend?: boolean;
  firstSeenAt: string;
  lastMessageAt: string | null;
  lastToggledAt: string | null;
}

/** Per-platform capability report from GET /channels/platforms (v2 Boos only). */
export interface ChannelPlatformStatus {
  platform: ChannelPlatform;
  /** Whether this Boo has the platform configured right now. */
  enabled: boolean;
  /** Whether the Boo can list the platform's channels/people for the add pickers. */
  directorySupported: boolean;
  surfaces: ChannelSurfaceType[];
  /**
   * Whether Boo can post into channels on this platform, i.e. whether a
   * channel there can be the default channel. Absent on Boos that predate the
   * default channel.
   */
  postsToChannels?: boolean;
  /** Whether Boo can create a channel on this platform itself. */
  createsChannels?: boolean;
}

/**
 * The deployment's default channel: where Boo posts when nothing else names a
 * place (scheduled reports, announcements, team-wide posts). One for the whole
 * Boo, not one per platform. Posting there ignores the channel's answering
 * mode, so a quiet channel can still be the default.
 */
export interface DefaultChannel {
  platform: ChannelPlatform;
  channelOrSpaceId: string;
  displayName: string | null;
  surfaceType: string;
  setAt: string;
}

/** Where team-wide posts go while no default channel is set: the installer's DM. */
export interface DefaultChannelFallback {
  kind: "installer_dm";
  platform: ChannelPlatform;
  providerUserId: string;
  displayName: string | null;
}

export interface DefaultChannelResponse {
  defaultChannel: DefaultChannel | null;
  fallback?: DefaultChannelFallback | null;
}

export interface CreatedChannel {
  channelOrSpaceId: string;
  displayName: string | null;
  surfaceType: string;
  /** Non-fatal notes, e.g. the installer could not be invited. The channel exists. */
  warnings?: string[];
}

export interface DirectoryChannelRow {
  channelOrSpaceId: string;
  displayName: string | null;
  surfaceType: string;
  /** Already present in the channel list. */
  tracked: boolean;
}

export interface DirectoryUserRow {
  providerUserId: string;
  displayName: string | null;
  tracked: boolean;
}

export interface ChannelDirectoryResponse {
  /** false = this Boo can't list the platform (credentials live elsewhere). */
  supported: boolean;
  channels: DirectoryChannelRow[];
  users: DirectoryUserRow[];
  /** Partial-failure notes (missing scope) — the rest still serves. */
  warnings: string[];
  /**
   * The brain walks the platform in the background. done=false means rows
   * are still arriving (counts are what it has so far); poll until done.
   * Absent on older brains.
   */
  progress?: { done: boolean; channels: number; users: number };
}

/**
 * GET /channels/meta — live single-id lookup (no cache, no directory cap).
 * 404 (code "not_found") = the platform doesn't know the id — or the lookup
 * itself failed (revoked credentials); the brain doesn't distinguish.
 */
export interface ChannelMetaResponse {
  /** false = this Boo can't resolve ids on the platform; other fields absent. */
  supported: boolean;
  platform?: ChannelPlatform;
  channelOrSpaceId?: string;
  /** Slack DMs resolve with null — only the message path can label a DM. */
  displayName?: string | null;
  surfaceType?: string | null;
}

export interface ChannelsResponse {
  respondByDefault: boolean;
  channels: ChannelRow[];
  /**
   * null = no default channel set. Absent (undefined) = the Boo predates the
   * default channel, so the dashboard hides it.
   */
  defaultChannel?: DefaultChannel | null;
  /** Present while defaultChannel is null and the installer is known. */
  fallback?: DefaultChannelFallback | null;
  // v2 fields — all absent when the Boo predates channel-management v2; the
  // UI feature-detects on surfaceDefaults and falls back to the boolean flow.
  surfaceDefaults?: Record<ChannelSurfaceType, ChannelMode>;
  /** Which surface types each platform can actually produce. */
  platformSurfaces?: Record<ChannelPlatform, ChannelSurfaceType[]>;
  blockNewUsers?: boolean;
  /** null = the Boo's built-in rejection text. */
  rejectionMessage?: string | null;
  /**
   * true = this Boo supports channel-scoped user blocks. Older Boos silently
   * drop the channel field and would block the person everywhere — never send
   * a channel-scoped toggle unless this is true.
   */
  userChannelBlocks?: boolean;
  users?: ChannelUserRow[];
  _permissions: Permissions;
}

export type ThreadFlagAccess = "auto" | "approval" | "disabled";

/**
 * One thread mode from GET /flags — a per-conversation ability people can ask
 * Boo to switch on (deep thinking today). The registry is brain-side and
 * grows without dashboard changes; render the list dynamically.
 */
export type BooFlagKind = "thread-mode" | "feature";

export interface ThreadFlagRow {
  id: string;
  /**
   * "thread-mode" = a per-conversation toggle people ask Boo for (deep
   * thinking); "feature" = a Boo-wide capability (runtime feedback to Boo
   * Lab, the coding-assistant bridge). Absent on Boos that predate the field —
   * treat as "thread-mode".
   */
  kind?: BooFlagKind;
  /**
   * false = the flag is on/off only (auto | disabled), no approval card and
   * no approvers; the brain rejects "approval" for it. Absent on Boos that
   * predate the field — treat as true.
   */
  approvable?: boolean;
  title: string;
  summary: string;
  /**
   * auto = Boo switches it on when asked, no approval card;
   * approval = the normal approval card in chat — `approvers` says whose
   * Approve click counts (not who may ask);
   * disabled = requests are declined. Switching a mode OFF is always allowed
   * for everyone. Untouched flags come back as disabled + "everyone" — an
   * extra is off until an admin enables it here.
   */
  access: ThreadFlagAccess;
  /** "everyone" or `${platform}:${providerUserId}` keys — the channel-user vocabulary. */
  approvers: "everyone" | string[];
}

export interface ThreadFlagsResponse {
  flags: ThreadFlagRow[];
}

// Identity v2: what each group may use in chat (GET/PUT /roles) and one row
// per human (GET /people). A 409 "brain_outdated" on /roles marks a Boo
// that keeps the flags and channel-user screens.
export type FeatureLevel = "auto" | "confirm" | "disabled";

export interface RoleFeature {
  id: string;
  title: string;
  summary: string;
  kind: string;
  approvable: boolean;
}

export interface RoleDefinition {
  features: Record<string, FeatureLevel>;
}

export interface RolesUpgrade {
  completedAt: string;
  dropped?: string[];
}

export interface RolesResponse {
  version: number;
  roles: Record<string, RoleDefinition>;
  features: RoleFeature[];
  updatedBy: string | null;
  updatedAt: string | null;
  upgrade?: RolesUpgrade | null;
  _permissions?: BooPermissions;
}

export interface RolesUpdate {
  roles: Record<string, RoleDefinition>;
}

export interface PersonHandle {
  provider: ChannelPlatform;
  workspace: string;
  providerUserId: string;
  source: "install" | "manual" | "email" | "seen";
}

export interface PersonRow {
  personId: string;
  kind: "managed" | "chat_only";
  name: string;
  email?: string | null;
  booRole: BooRole | null;
  orgRole?: string | null;
  base: "admin" | "member";
  groups: string[];
  keptGroups?: string[];
  blocked: boolean;
  viewAllSpend?: boolean;
  lastActive: string | null;
  handles: PersonHandle[];
}

export interface PendingInvite {
  invitationId: string;
  email: string;
  booRole: BooRole;
  invitedBy?: BooPerson | null;
  expiresAt?: string | null;
}

export interface PeopleResponse {
  data: PersonRow[];
  pendingInvites?: PendingInvite[];
  myRole?: BooRole | null;
  ownerless?: boolean;
  _permissions: BooPermissions;
}

export interface PersonUpdate {
  role?: string;
  blocked?: boolean;
  viewAllSpend?: boolean;
}

export type HandleRef = Pick<PersonHandle, "provider" | "workspace" | "providerUserId">;

// Boo access an invitation hands out: a level on one Boo. Owner and Admin
// need a Boo owner, as on the People tab.
export interface BooAccessGrant {
  containerId: string;
  role: BooRole;
}

// ── Boo-to-Boo connections ───────────────────────────────────────────
// Boo-to-Boo access, by scope. A Boo (the consumer) asks another in the same
// org (the source) for scopes; the source's admins grant all or some; the
// consumer uses a subset of the grant. Shapes mirror boo-backend
// lib/boo-scopes.ts and routes/connections.ts.

export type ChannelRef = { provider: string; channelId: string };
export type PersonRef = { provider: string; providerUserId: string };
/** Every item (including ones that appear later), or a listed set. */
export type ScopeChoice<T> = { all: true } | { only: T[] };

export interface GrantScopes {
  // A skill set's registry id (the whole set, including skills added later)
  // or "<registry>/<skill>" for one skill in it.
  skills: ScopeChoice<string>;
  memories: {
    org: boolean;
    channels: ScopeChoice<ChannelRef>;
    people: ScopeChoice<PersonRef>;
  };
}

export type GrantPreset = "essentials" | "team" | "full";
export type GrantDepth = GrantPreset | "custom" | "none";
export type GrantDecidedVia = "boo_admin" | "auto" | "org_owner" | "rollout" | "expired";

/** What a source could share right now, by scope. */
/** One skill, as its source describes it. `secrets` are the names of the source's saved secrets it reads. */
export interface SkillInfo {
  name: string;
  displayName: string;
  description: string;
  secrets: string[];
  // Its definition doesn't validate, so it can't run anywhere.
  invalid?: boolean;
}

export interface Grantable {
  skills: Array<{ registry: string; namespace: string; count: number; names: string[]; items?: SkillInfo[] }>;
  // origins: Boos some of a group's memories were reshared from.
  memories: {
    org: { count: number; titles: string[]; origins?: string[] };
    channels: Array<ChannelRef & { name?: string; count: number; titles: string[]; origins?: string[] }>;
    people: Array<PersonRef & { name?: string; count: number; titles: string[]; origins?: string[] }>;
  };
}

export type BooLinkStatus = "requested" | "approved" | "declined" | "revoked";

export interface BooRef {
  id: string;
  name: string;
  running: boolean;
}

// One Boo-to-Boo link as the backend records it. On the consumer's tab it is
// in `using`; on the source's tab in `sharing`.
export interface BooLinkRow {
  id: string;
  status: BooLinkStatus;
  // The edge id both Boos use; null until set up.
  edge: string | null;
  source: BooRef | null;
  consumer: BooRef | null;
  requested: GrantScopes;
  requestedPreset: GrantDepth;
  requestNote: string | null;
  requestedBy: BooPerson | null;
  requestedAt: string | null;
  // Null until approved.
  granted: GrantScopes | null;
  grantedPreset: GrantDepth | null;
  // The consumer may pass memories it got this way on to Boos that learn from it.
  reshare: boolean;
  decidedBy: BooPerson | null;
  decidedAt: string | null;
  decidedVia: GrantDecidedVia | null;
  decisionNote: string | null;
  // What the consumer chose to use (null until set up), and what it gets.
  pick: GrantScopes | null;
  effective: GrantScopes | null;
  // A request for more, waiting for the source's admins.
  more: { scopes: GrantScopes; note: string | null; requestedBy: BooPerson | null; requestedAt: string } | null;
  setUp: boolean;
  // Counts in use: live from the Boo when it's running, else as recorded.
  skills: number;
  memories: number;
  // The consumer's last pull from the source. Null until its first pull, or when it isn't running.
  sync: { at: string | null; state: "ok" | "unreachable" | "refused" } | null;
  // Boos the source itself got some of the shared memories from.
  resharedFrom?: string[];
  syncState: "in_sync" | "busy";
  // Approved, and setting it up failed; it is retried when both Boos run.
  setUpFailed: boolean;
  // Access was taken away and the consumer hasn't been seen dropping it yet.
  removalPendingSince: string | null;
  // When the waiting request (first, or for more) is declined unanswered.
  expiresAt: string | null;
}

export interface ConnectionsResponse {
  using: BooLinkRow[];
  sharing: BooLinkRow[];
  _permissions: { canView: boolean; canManage: boolean } | null;
}

// One arrow on the Boo map: `sourceId` shares with `consumerId`.
export interface BooMapLink {
  id: string;
  sourceId: string;
  consumerId: string;
  // "requested" only reaches org owners/admins and the Boos' admins.
  status: "approved" | "requested";
  setUp: boolean;
  skills: boolean;
  memories: boolean;
  skillCount: number;
  memoryCount: number;
  depth: GrantDepth | null;
}

export interface AccessOverview {
  boos: Array<BooRef & { owners: BooPerson[] }>;
  links: BooLinkRow[];
  _permissions: { canView: boolean; canOverride: boolean };
}

export interface AccessLogEvent {
  id: string;
  kind: BooAccessEventKind;
  boo: { id: string; name: string | null };
  actor: BooPerson | null;
  target: BooPerson | null;
  role: string | null;
  previousRole: string | null;
  reason: string | null;
  details: Record<string, unknown> | null;
  // An org owner acted for the Boo's own people.
  override: boolean;
  createdAt: string;
}

export type AccessLogCategory = "sharing" | "roles" | "handover" | "lifecycle";
export type AccessLogQuery = { category?: AccessLogCategory; booId?: string; overrides?: boolean; before?: string; limit?: number };

// ── Audit log (GET /api/org/audit, /api/containers/:id/audit) ───────────────

export type AuditCategory =
  | "access"
  | "members"
  | "sign_in"
  | "sso"
  | "connectors"
  | "integrations"
  | "secrets"
  | "spending"
  | "org"
  | "billing"
  | "staff"
  | "boo_activity";

export type AuditOutcome = "success" | "denied" | "error";

// One event as the read API returns it. `action` is a catalog key such as
// "member.role_changed"; unknown keys still render (audit-copy.ts falls back).
export type AuditEventView = {
  id: string;
  action: string;
  category: string;
  createdAt: string;
  boo: { id: string; name: string | null } | null;
  // type: user | person | boo | agent_key | staff | system
  actor: { type: string; id: string | null; label: string | null; email: string | null };
  onBehalfOf: { userId: string; name: string | null; email: string | null } | null;
  target: { type: string; id: string | null; label: string | null } | null;
  outcome: AuditOutcome;
  reason: string | null;
  via: string | null;
  source: string;
  ip: string | null;
  userAgent: string | null;
  metadata: Record<string, unknown> | null;
};

export type AuditPage = { data: AuditEventView[]; nextBefore: string | null };

export type AuditQuery = {
  category?: AuditCategory;
  // Exact key, or a `prefix.*`.
  action?: string;
  // The actor's id (a userId for people with an account).
  actor?: string;
  booId?: string;
  targetType?: string;
  targetId?: string;
  outcome?: AuditOutcome;
  // ISO timestamps.
  from?: string;
  to?: string;
  before?: string;
  limit?: number;
  // Super admins only: add Boo Lab's internal events.
  includeInternal?: boolean;
};

export interface HandoverPreview {
  boo: BooRef;
  roles: Array<BooPerson & { role: BooRole; inOrg: boolean }>;
  candidates: Array<BooPerson & { orgRole: string }>;
  // People the Boo has 1-on-1 memories about. Null when it can't list them.
  subjects: Array<PersonRef & { name: string | null; count: number }> | null;
  links: { using: number; sharing: number };
  // Whether the caller acts as the Boo's owner or as an org owner (reason required).
  via: "boo_owner" | "org_owner";
}

export interface HandoverPlan {
  toUserId: string;
  others: Array<{ userId: string; role: BooRole | "remove" }>;
  rename: string | null;
  archive: PersonRef[];
  reason: string | null;
}


export interface ChannelCostRow {
  chatPlatform: string;
  channelId: string;
  isDm: boolean;
  /** Cost per customer bucket (ai / services). */
  buckets: Record<string, number>;
  total: number;
  /** Raw per-service costs (search, e2b, image, tts, ai…) — super admins only. */
  services?: Record<string, number>;
}

export interface ChannelCostsResponse {
  containerId: string;
  /** Start of the current calendar month — costs accrue from here. */
  periodStart: string;
  currency: string;
  /** The org's `channel-wise-spend` flag: whether customers see this column. */
  customerVisible: boolean;
  channels: ChannelCostRow[];
  _permissions: Permissions;
}

// Org-level rights from GET /api/org. Keys a backend doesn't send yet are absent.
export type OrgPermissions = Record<string, boolean> & {
  canViewUsage?: boolean;
  canViewBilling?: boolean;
  canViewSpendLimits?: boolean;
  canViewAccess?: boolean;
  canOverrideAccess?: boolean;
  // Org owners and admins, and Boo Lab staff: the org's Audit log.
  canViewAudit?: boolean;
};

export interface OrgPermissionsResponse {
  _permissions: OrgPermissions | null;
}

export type OrgRole = "owner" | "admin" | "billing_admin" | "member";

export interface OrgMemberRow {
  id: string;
  role: string;
  createdAt: string;
  user: { id: string; name: string; email: string };
}

export interface OrgInvitationRow {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  emailError?: string | null;
}

// The active org as the dashboard sees it. `myRole` is the caller's
// effective role: a super admin is "owner" in every org.
export interface OrgDetails {
  id: string;
  name: string;
  slug: string | null;
  // Inline image (data: URL) or null. Shown in the dashboard only.
  logo: string | null;
  myRole: OrgRole;
  members: OrgMemberRow[];
  invitations: OrgInvitationRow[];
}

export interface OrgDetailsResponse {
  data: OrgDetails;
  _permissions: Permissions;
}

export type WaitlistStatus = "pending" | "approved" | "rejected";
export type WaitlistStatusFilter = WaitlistStatus | "all";

export interface AdminWaitlistEntry {
  id: string;
  orgLabel: string;
  requestedByEmail: string | null;
  requestedName: string | null;
  orgInfo: string | null;
  orgWebsite: string | null;
  agentType: string;
  status: WaitlistStatus;
  submittedAt: string;
  reviewedAt: string | null;
  matchedOrg: { id: string; name: string | null; via: "gchat" | "teams" } | null;
}

export interface OnboardingState {
  step: "welcome" | "organization" | "waitlist" | "provisioning" | "connect" | "complete";
  // When step === "waitlist": when the signup was placed on the waitlist.
  submittedAt?: string;
  // Source-of-truth active org for onboarding steps. better-auth's
  // useSession() caches activeOrganizationId on the client and doesn't
  // refresh on setActive — reading it from there during onboarding can
  // lag a step behind. Sourcing it from /state keeps every field here
  // fresh from the same server read.
  activeOrganizationId?: string;
  containerId?: string;
  // The container row's name — canonical display string for "your Boo"
  // copy on the provisioning + connect screens. PreviewProvider's
  // in-memory `agentName` resets on refresh; this doesn't.
  containerName?: string | null;
  // Which onboarding branch to render once the row exists. Before that the
  // client falls back to the stored entry-path intent (lib/posture.ts).
  posture?: Posture;
  containerStatus?: string;
  containerError?: string;
  // Container fields surfaced so the onboarding step can render the
  // Google Chat custom-setup form (which needs the events URL) without
  // a separate /containers/list call. railwayServiceUrl is set as soon
  // as the brain has a Railway domain; customDomain* fields settle
  // independently once ACME finishes.
  railwayServiceUrl?: string | null;
  customDomainStatus?: "pending" | "verifying" | "active" | "error" | null;
  customDomainFqdn?: string | null;
  slackConnected?: boolean;
  // Set for both Slack paths (shared Boo app and custom app); opens the
  // workspace from the success screen.
  slackTeamId?: string | null;
  // The shared Slack app is linked but Boo's greeting is held until the
  // default-channel step releases it (api.onboarding.kickoff).
  slackKickoffPending?: boolean;
  gchatConnected?: boolean;
  teamsConnected?: boolean;
  discordConnected?: boolean;
  canRetry?: boolean;
  retryCount?: number;
}

export interface OnboardingSuggestions {
  orgName: string | null;
  orgWebsite: string | null;
  agentName: string | null;
  orgInfo: string | null;
}

export interface ContainerLogs {
  buildLines: string[];
  runtimeLines: string[];
}

interface InstallUrlResponse {
  url: string;
}

interface AdminPermissionsResponse {
  policies: Record<string, Record<string, readonly string[]>>;
}

// ── HTTP helpers ──────────────────────────────────────────────────

const BASE = import.meta.env.VITE_API_URL;

export class ApiError extends Error {
  status: number;
  code: string;
  // The parsed JSON error body, when there was one.
  body?: unknown;

  constructor(status: number, code: string, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

// Routes that answer { error: <message>, code: <code> } (connectors, org
// settings) carry the code in the body; older ones put it in `error`.
export function apiErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const code = (err.body as { code?: unknown } | undefined)?.code;
  return typeof code === "string" ? code : err.code;
}

// The one place the backend is called: session cookie, network-failure
// mapping, and non-2xx → ApiError. Both the JSON and the binary paths go
// through it so the auth contract cannot drift between them.
async function rawFetch(path: string, opts: RequestInit = {}): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { credentials: "include", ...opts });
  } catch {
    throw new ApiError(
      0,
      "NETWORK_ERROR",
      "Unable to reach the server. Check your connection and try again.",
    );
  }
  if (!res.ok) throw await errorFromResponse(res);
  return res;
}

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await rawFetch(path, {
    ...opts,
    headers: { "Content-Type": "application/json", ...opts.headers },
  });
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (null as T);
}

async function errorFromResponse(res: Response): Promise<ApiError> {
  const text = await res.text();
  let code = `HTTP ${res.status}`;
  let body: unknown;
  try {
    const parsed = JSON.parse(text) as { error?: string };
    body = parsed;
    code = parsed.error ?? code;
  } catch {
    /* not JSON */
  }
  return new ApiError(res.status, code, friendlyErrorMessage(res.status, body, text), body);
}

// Binary download (feedback archives): the body comes back as a Blob for an
// object-URL save.
async function requestBlob(path: string): Promise<Blob> {
  const res = await rawFetch(path);
  return res.blob();
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}
function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: "PUT",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}
function patch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
}
function del<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: "DELETE", body: body !== undefined ? JSON.stringify(body) : undefined });
}

function accessLogQuery(q: AccessLogQuery): string {
  const search = new URLSearchParams();
  if (q.category) search.set("category", q.category);
  if (q.booId) search.set("booId", q.booId);
  if (q.overrides) search.set("overrides", "1");
  if (q.before) search.set("before", q.before);
  if (q.limit) search.set("limit", String(q.limit));
  const text = search.toString();
  return text ? `?${text}` : "";
}

function auditQuery(q: AuditQuery, extra: Record<string, string> = {}): string {
  const search = new URLSearchParams(extra);
  for (const key of ["category", "action", "actor", "booId", "targetType", "targetId", "outcome", "from", "to", "before"] as const) {
    const v = q[key];
    if (v) search.set(key, v);
  }
  if (q.limit) search.set("limit", String(q.limit));
  if (q.includeInternal) search.set("includeInternal", "1");
  const text = search.toString();
  return text ? `?${text}` : "";
}

function usageReportQuery(
  period: UsageReportPeriod,
  filter?: UsageReportFilter,
): URLSearchParams {
  const search = new URLSearchParams({ period });
  if (filter?.channelId) search.set("channelId", filter.channelId);
  if (filter?.threadId) search.set("threadId", filter.threadId);
  if (filter?.userId) search.set("userId", filter.userId);
  if (filter?.purpose) search.set("purpose", filter.purpose);
  if (filter?.kind) search.set("kind", filter.kind);
  return search;
}

// Encode a vault secret path segment-by-segment so real "/" hierarchy
// separators survive while any reserved characters inside a segment are
// escaped. The brain decodes each segment with decodeURIComponent, so this
// must encode per-segment rather than the whole string.
function encodeVaultPath(path: string): string {
  return path.split("/").map(encodeURIComponent).join("/");
}

// ── Runtime feedback reports (admin) ─────────────────────────────

// A Boo files one of these about itself when its own tools, skills, scheduled
// tasks, working space, or reasoning misbehave (gated by the runtime_feedback
// flag). Packages carry the customer conversation for that turn, so they are
// super-admin only. Shapes mirror the brain's manifest.json verbatim.

export type FeedbackCategory =
  | "tool"
  | "skill"
  | "scheduled_task"
  | "automation"
  | "sandbox"
  | "permissions"
  | "reasoning"
  | "other";

export interface FeedbackReportEntry {
  filedAt: string;
  /** 1-3 sentences. */
  summary: string;
  category: FeedbackCategory | string;
  expected?: string;
  observed?: string;
  suspects?: string[];
}

export interface FeedbackManifest {
  reportId: string;
  filedAt: string;
  sealedAt: string;
  brain: {
    version: string;
    gitSha: string;
    builtAt: string;
    instanceId: string;
    containerId?: string;
  };
  thread: {
    threadId: string;
    provider: string;
    providerThreadId: string;
    channelId?: string;
    kind: "chat" | "heartbeat" | "daemon" | string;
    heartbeatTaskId?: string;
    daemonId?: string;
  };
  turn: {
    turnId: string;
    turnKind: "run" | "resume" | "subagent" | string;
    modelSelection?: { purpose: string; modelName: string; source: string };
    outcome?: "completed" | "interrupted" | "failed" | "aborted" | string;
    durationMs?: number;
    costUsd?: number;
    tokens?: number;
  };
  /** 1..3 entries — one archive per turn. */
  reports: FeedbackReportEntry[];
  sandbox?: { kind: string; template?: string; sandboxId?: string };
  flags?: {
    thread?: Record<string, { model?: string; enabledBy?: string; enabledAtIso?: string }>;
    access?: Record<string, { access: ThreadFlagAccess; approvers: "everyone" | string[] }>;
  };
  experiments?: {
    configured: boolean;
    distinctId: string;
    resolutions: Array<{ id: string; value: unknown; source: string }>;
    lastPollAt?: number;
    lastError?: string;
  };
  bundles?: Array<{ id: string; version?: string; digest?: string; completedDigest?: string }>;
  skills?: {
    activated: Array<{
      ref: string;
      registryId: string;
      namespace: string;
      name: string;
      snapshot: string;
      sourceRef: string;
      files: number;
    }>;
    unresolved: unknown[];
  };
  catalog?: Record<string, { headSha?: string; files?: number }>;
  usage?: { rows: number; entries: unknown[] };
  capture?: {
    events: number;
    messages: number;
    capturedBytes: number;
    droppedMessages: number;
    skippedFiles: string[];
  };
  archive?: { entries: string[] };
  /** Present only once an operator archived the report; absent means open. */
  review?: FeedbackReview;
}

// An operator's verdict on a report, kept by the Boo beside the manifest.
// Only "archived" is ever stored: reopening removes the block. `by` is the
// super-admin's email, stamped by boo-backend.
export interface FeedbackReview {
  state: "archived";
  at: string;
  comment?: string | null;
  by?: string | null;
}

/** Which reports a list answers: open (default), archived, or all. */
export type FeedbackListState = "open" | "archived" | "all";

export type FeedbackReviewUpdate = { state: "open" } | { state: "archived"; comment?: string };

/** The Boo's ceiling on an archive comment. */
export const FEEDBACK_COMMENT_MAX = 2000;

export interface AdminFeedbackSummary {
  container: AdminFeedbackReportRow["container"];
  reports: AdminFeedbackReportRow[];
  /** Every report on the Boo regardless of filter; null on a Boo that predates archiving. */
  counts: { open: number; archived: number } | null;
  /** Page size the backend asked the brain for, not a ceiling on how many reports exist. */
  limitPerBrain: number;
  state: FeedbackListState;
  /** Present while older reports remain; pass it back as `cursor` for the next page. */
  nextCursor?: string;
}

/** One list row: the manifest columns the table needs, tagged with the Boo it came from. */
export interface AdminFeedbackReportRow {
  reportId: string;
  filedAt: string;
  sealedAt: string | null;
  container: { id: string; name: string; organizationId: string; organizationName: string };
  category: FeedbackCategory | string;
  summary: string;
  reportCount: number;
  thread: { kind: string; provider: string; threadId: string; channelId: string | null };
  brain: { version: string; gitSha: string; instanceId: string };
  turn: {
    turnKind: string;
    outcome: string | null;
    durationMs: number | null;
    costUsd: number | null;
    tokens: number | null;
    modelName: string | null;
  };
  capture: { events: number; messages: number; capturedBytes: number; droppedMessages: number };
  sandbox: { kind: string; template: string | null } | null;
  review: FeedbackReview | null;
}

// ── Automation API keys (admin) ───────────────────────────────────

export interface AgentKeyCapability {
  id: string;
  label: string;
  description?: string;
}

export interface AgentKeyCapabilityCategory {
  id: string;
  label: string;
  capabilities: AgentKeyCapability[];
}

export interface AgentKeyRow {
  id: string;
  label: string;
  capabilities: string[];
  createdByUserId: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  expiresAt: string | null;
}

export interface SelfHostedBoo {
  id: string;
  name: string;
  organizationId: string;
  brainUrl: string | null;
  createdAt: string;
}

// Create and rotate carry the raw integrations token, the only time it is
// returned, and the backend address the brain attests to.
export interface SelfHostedBooToken extends SelfHostedBoo {
  token: string;
  integrationsAddr: string;
}

// Create response carries the raw token exactly once, in `key`, plus a
// copyable usage guide tailored to the granted permissions.
export interface CreateAgentKeyResponse extends AgentKeyRow {
  key: string;
  usageGuide: string;
}

export interface Ms365Status {
  connected: boolean;
  account: string | null;
  tenantId: string | null;
  containerId: string | null;
  // Graph scopes currently granted on the connection — drives the permission
  // picker's "granted" column and the re-authorise (dirty) detection.
  grantedScopes: ReadonlyArray<string>;
  _permissions: Permissions;
}

// ── App sign-in (per-org identity provider for apps Boos build) ─────

export type SsoUpstream = "google" | "microsoft" | "slack" | "email";
export type SsoPolicyMode = "members" | "org_domain" | "allowlist" | "roster" | "open";

export interface SsoSettings {
  issuer: string;
  enabledUpstreams: SsoUpstream[];
  extraDomains: string[];
  source: "custom" | "detected";
  detected: Array<{ upstream: SsoUpstream; reason: string }>;
  ownerDomain: string | null;
  availableUpstreams: SsoUpstream[];
}

export interface SsoSettingsResponse {
  data: SsoSettings | null;
  enabled: boolean;
  _permissions: { canEdit: boolean };
}

export interface SsoPolicy {
  mode: SsoPolicyMode;
  domains: string[];
  emails: string[];
  upstreams: SsoUpstream[];
  source: "custom" | "default";
}

export interface SsoPolicyInput {
  mode: SsoPolicyMode;
  domains: string[];
  emails: string[];
  upstreams: SsoUpstream[];
}

export interface SsoApp {
  appId: string;
  // Shown on the app's hosted sign-in page; null = the app id is shown.
  name: string | null;
  description: string | null;
  clientId: string;
  containerId: string | null;
  containerName: string | null;
  redirectUris: string[];
  createdAt: string;
  policy: SsoPolicy;
}

export interface SsoAppsResponse {
  data: SsoApp[];
  _permissions: { canEdit: boolean };
}

// One change to app sign-in, by a person in the dashboard or by the Boo.
// before/after hold the settings (enabledUpstreams, extraDomains, source),
// the policy (SsoPolicy), the sign-in page text (name, description), the new
// app's text and redirectUris (client_registered, before null) or the owning
// Boo (containerId, ownership_taken).
export type SsoAuditEvent =
  | "settings_updated"
  | "settings_reset"
  | "policy_updated"
  | "policy_reset"
  | "app_updated"
  | "client_registered"
  | "ownership_taken";

export interface SsoAuditEntry {
  id: string;
  containerId: string | null;
  appId: string | null;
  appName: string | null;
  event: SsoAuditEvent;
  actor: string;
  actorType: "user" | "agent";
  actorName: string | null;
  actorEmail: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  createdAt: string;
}

export interface SsoAuditPage {
  data: SsoAuditEntry[];
  // Pass as `before` for the next (older) page; null on the last one.
  nextBefore: string | null;
}

export interface AdminSsoResponse {
  data: {
    boos: Array<{ containerId: string; containerName: string | null; settings: SsoSettings }>;
    apps: SsoApp[];
  } | null;
  enabled: boolean;
}

// ── Code hosts ──────────────────────────────────────────────────────

export type CodeHostKind = "github" | "gitlab";
export type CodeHostScope = "boo" | "workspace";

export interface CodeHostConnection {
  id: string;
  host: CodeHostKind;
  method: "github_app" | "service_account" | "oauth" | string;
  scope: CodeHostScope;
  containerId: string | null;
  baseUrl: string;
  hostLabel: string; // "github.com", "gitlab.com", "gitlab.example.com"
  namespace: string; // GitHub login or GitLab group path; "" until chosen
  accountType: string | null;
  repositorySelection: "all" | "selected" | string | null;
  botUsername: string | null;
  status: "pending_auth" | "pending_group" | "active" | "suspended" | "revoked" | string;
  revokedReason: string | null;
  tokenExpiresAt: string | null;
  manageUrl: string | null;
  createdAt: string;
  isDefault: boolean;
  // Workspace pool only: the Boos granted this connection.
  booIds?: string[];
}

export interface CodeHostsResponse {
  // What the Boo can use (empty on the workspace-only listing).
  connections: CodeHostConnection[];
  // The workspace pool (every workspace-scoped connection).
  workspace: CodeHostConnection[];
  defaultConnectionId: string | null;
  brainConfigured?: boolean;
  canManageWorkspace: boolean;
  githubAvailable: boolean;
  gitlabOAuthAvailable: boolean;
}

export interface GitlabGroupOption {
  id: string;
  path: string;
  name: string;
  url: string;
}

export type GitlabConnectResult =
  | { connected: true; connection: CodeHostConnection }
  | { connected: false; needsGroup: true; connectionId: string; groups: GitlabGroupOption[] };

// Where one app bundle's source lives, chosen at install.
export type BundleSource = "repository" | "managed";

// ── API client ────────────────────────────────────────────────────

// Vendor experiment flags (PostHog-backed) for the brain fleet — the
// brain-exp-* prefix is enforced server-side.
export interface ExperimentRow {
  id: number;
  key: string;
  description: string;
  active: boolean;
  // null = targeting was customized in PostHog (condition groups/variants);
  // rollout and pins are then edited there, not here.
  rolloutPercentage: number | null;
  // Brains served the flag regardless of rollout (container ids).
  pinnedContainerIds: string[];
  conditionGroups: number;
  createdAt: string;
  createdBy: string | null;
}

// What PostHog currently serves for one brain's distinct_id — the brain
// applies it on its next poll, or falls back to in-code defaults when
// PostHog is unreachable.
export interface ExperimentResolution {
  containerId: string;
  name: string;
  flags: Record<string, boolean | string>;
  evaluatedAt: string;
}

// Ground truth from the brain deployment itself — the values it is actually
// running. `source: "default"` = the in-code fail-safe, not PostHog.
export interface AppliedExperimentEntry {
  id: string;
  flagKey: string;
  summary: string;
  defaultValue: boolean | string;
  value: boolean | string;
  source: "posthog" | "default";
}

export interface AppliedExperiments {
  experiments: AppliedExperimentEntry[];
  posthog: {
    configured: boolean;
    distinctId: string;
    // Last SUCCESSFUL poll (epoch ms); absent until one succeeds.
    lastPollAt?: number;
    // Present while polls fail — applied values are then a last-known
    // snapshot, not live.
    lastError?: string;
  };
}

// PUT …/variables (Boo page, setup page or one-time link): runs verify with
// the stored values merged with these and saves only when every check passes.
// A secret left out keeps its stored value.
async function putConnectorVariables(path: string, values: Record<string, string>): Promise<ConnectorSaveResult> {
  try {
    return { saved: true, ...(await put<ConnectorVerifyResult>(path, { values })) };
  } catch (err) {
    if (!(err instanceof ApiError) || (err.status !== 422 && err.status !== 400)) throw err;
    const body = err.body as
      | { code?: string; error?: string; account?: string | null; checks?: ConnectorCheck[]; errors?: Record<string, string> }
      | undefined;
    const code = body?.code ?? body?.error;
    if (code === "verify_failed" && body?.checks) return { saved: false, code, account: body.account ?? null, checks: body.checks };
    if (code === "invalid_variables" && body?.errors) return { saved: false, code, errors: body.errors };
    throw err;
  }
}

export const api = {
  models: {
    // Organization: any member reads; owners and admins change.
    org: () => request<OrgModels>("/api/org/models"),
    setOrg: (policy: { defaults: Record<string, string | null> }) => put<{ ok: true }>("/api/org/models", policy),
    setAvailability: (models: Record<string, boolean>) =>
      put<{ disabledModels: string[] }>("/api/org/models/availability", { models }),
    saveEndpoint: (name: string, input: OrgEndpointInput) =>
      put<{ endpoint: OrgEndpointView }>(`/api/org/models/endpoints/${encodeURIComponent(name)}`, input),
    deleteEndpoint: (name: string) => del<{ ok: true }>(`/api/org/models/endpoints/${encodeURIComponent(name)}`),
    // Runs on one of the org's running Boos; the answer names it.
    probeEndpoint: (body: EndpointProbeRequest) =>
      post<{ probeId: string; booId: string; booName: string }>("/api/org/models/endpoints/probe", body),
    getEndpointProbe: (probeId: string) =>
      request<ProbeReport>(`/api/org/models/endpoints/probe/${encodeURIComponent(probeId)}`),
    // One Boo.
    boo: (containerId: string) => request<BooModels>(`/api/containers/${containerId}/models`),
    booCatalog: (containerId: string) =>
      request<BooModelCatalog>(`/api/containers/${containerId}/models/catalog`),
    setBoo: (containerId: string, models: Record<string, string | null>) =>
      put<ModelSaveResult>(`/api/containers/${containerId}/models`, { models }),
    deleteBooProvider: (containerId: string, name: string) =>
      del<ModelSaveResult>(`/api/containers/${containerId}/models/providers/${encodeURIComponent(name)}`),
    moveBooProviderToOrg: (containerId: string, name: string) =>
      post<{ endpoint: OrgEndpointView }>(
        `/api/containers/${containerId}/models/providers/${encodeURIComponent(name)}/move-to-org`,
      ),
  },
  org: {
    permissions: () => request<OrgPermissionsResponse>("/api/org"),
    // The org's audit log (canViewAudit: org owners and admins, Boo Lab staff).
    audit: {
      list: (q: AuditQuery = {}) => request<AuditPage>(`/api/org/audit${auditQuery(q)}`),
      export: (format: "csv" | "jsonl", q: AuditQuery = {}) =>
        requestBlob(`/api/org/audit/export${auditQuery({ ...q, before: undefined, limit: undefined }, { format })}`),
    },
    subdomain: () => request<{ data: OrgSubdomain }>("/api/org/subdomain"),
    // Every read and change of the active org goes through boo-backend, not
    // the better-auth client: it decides who may act (including super admins
    // in orgs they are not a member of) and accepts our own roles such as
    // billing_admin, which better-auth's inviteMember/updateMemberRole reject.
    details: () => request<OrgDetailsResponse>("/api/org/details"),
    // Any member reads them; owners and admins change them.
    settings: () => request<OrgSettings>("/api/org/settings"),
    updateSettings: (body: { connectorSetup: ConnectorSetupPolicy }) => patch<OrgSettings>("/api/org/settings", body),
    // Rename and/or replace the logo (a data: URL, or null to clear).
    update: (body: { name?: string; logo?: string | null }) =>
      patch<{ data: { id: string; name: string; slug: string | null; logo: string | null } }>("/api/org", body),
    invite: (body: { email: string; role: string; resend?: boolean; boos?: BooAccessGrant[] }) =>
      post<unknown>("/api/org/invitations", body),
    cancelInvitation: (invitationId: string) =>
      del<{ ok: true }>(`/api/org/invitations/${encodeURIComponent(invitationId)}`),
    updateMemberRole: (userId: string, role: string) =>
      patch<unknown>(`/api/org/members/${userId}`, { role }),
    removeMember: (userId: string) =>
      del<{ ok: true }>(`/api/org/members/${encodeURIComponent(userId)}`),
  },
  orgs: {
    list: () => request<OrgRow[]>("/api/auth/organization/list"),
    create: (name: string, website?: string) =>
      post<OrgRow>(
        "/api/auth/organization/create",
        website ? { name, website } : { name },
      ),
    setActive: (organizationId: string) =>
      post<Session>("/api/auth/organization/set-active", { organizationId }),
    update: (
      organizationId: string,
      data: { name?: string; website?: string },
    ) =>
      post<OrgRow>("/api/auth/organization/update", { organizationId, data }),
  },
  admin: {
    users: () => request<ListResponse<UserRow>>("/api/admin/users"),
    updateUser: (
      id: string,
      data: Partial<Pick<UserRow, "name" | "role" | "banned">>,
    ) => patch<UserRow>(`/api/admin/users/${id}`, data),
    organizations: () =>
      request<ListResponse<OrgRow>>("/api/admin/organizations"),
    // Name/slug for a single org — used to label the frozen switcher when a
    // super-admin views an org that isn't in their own membership list.
    org: (id: string) =>
      request<{ data: { id: string; name: string; slug: string | null } }>(
        `/api/admin/organizations/${id}`,
      ),
    updateOrganization: (
      id: string,
      data: Partial<Pick<OrgRow, "name" | "slug" | "allowedAuthMethods" | "status">>,
    ) => patch<OrgRow>(`/api/admin/organizations/${id}`, data),
    // Read-only: can this org be torn down, and if not, which Boos block it.
    orgTeardownPreflight: (id: string) =>
      request<OrgTeardownPreflight>(
        `/api/admin/organizations/${id}/teardown-preflight`,
      ),
    // Soft-delete the org. Backend refuses (409) if it still has live Boos.
    deleteOrganization: (id: string) =>
      del<{ success: boolean; id: string }>(`/api/admin/organizations/${id}`),
    orgMembers: (orgId: string) =>
      request<ListResponse<OrgMember>>(
        `/api/admin/organizations/${orgId}/members`,
      ),
    orgApps: (orgId: string) =>
      request<AppRow[]>(`/api/admin/organizations/${orgId}/apps`),
    permissions: () =>
      request<AdminPermissionsResponse>("/api/admin/permissions"),
    orgsEnriched: () =>
      request<{ data: AdminOrgEnriched[] }>(
        "/api/admin/organizations/enriched",
      ),
    orgContainers: (orgId: string) =>
      request<AdminContainerRow[]>(
        `/api/admin/organizations/${orgId}/containers`,
      ),
    orgInvitations: (orgId: string) =>
      request<InvitationRow[]>(`/api/admin/organizations/${orgId}/invitations`),
    // `boos` is the Boo access the invite carries (applied when they accept);
    // left out on a resend, the pending invite keeps what it already has.
    createOrgInvitation: (
      orgId: string,
      data: { email: string; role: "admin" | "billing_admin" | "member"; resend?: boolean; boos?: BooAccessGrant[] },
    ) =>
      post<InvitationRow & { boos: BooAccessGrant[] }>(
        `/api/admin/organizations/${orgId}/invitations`,
        data,
      ),
    updateMemberRole: (orgId: string, userId: string, role: string) =>
      patch<OrgMember>(
        `/api/admin/organizations/${orgId}/members/${userId}`,
        { role },
      ),
    infrastructure: () =>
      request<InfrastructureOverview>("/api/admin/infrastructure/overview"),
    container: (id: string) =>
      request<AdminContainerDetail>(`/api/admin/containers/${id}`),
    infrastructureCosts: () =>
      request<AdminCosts>("/api/admin/infrastructure/costs"),
    reconcileStatuses: () =>
      post<{
        reconciled: Array<{
          id: string;
          name: string;
          from: string;
          to: string;
        }>;
        failed: Array<{ id: string; name: string; error: string }>;
      }>("/api/admin/infrastructure/reconcile", {}),
    setWarmPoolEnabled: (enabled: boolean) =>
      patch<{ enabled: boolean }>("/api/admin/warm-pool/settings", { enabled }),
    tearDownFailedPool: () =>
      post<{
        torndown: Array<{ id: string; name: string; agentType: string }>;
        failed: Array<{ id: string; name: string; error: string }>;
      }>("/api/admin/warm-pool/teardown-failed", {}),
    provisionPoolContainer: () =>
      post<{ accepted: boolean; agentType: string }>(
        "/api/admin/warm-pool/provision",
        { agentType: "boo-brain" },
      ),
    tearDownContainer: (id: string) =>
      del<{ success: boolean; id: string }>(`/api/admin/containers/${id}`),
    containerSandboxes: (id: string) =>
      request<{ sandboxes: AdminSandbox[]; truncated: boolean }>(`/api/admin/containers/${id}/sandboxes`),
    // Exhaustive, cursor-paginated single-state listing for the Sandboxes sub-page.
    containerSandboxesPage: (id: string, state: "running" | "paused", cursor?: string) => {
      const params = new URLSearchParams({ state });
      if (cursor) params.set("cursor", cursor);
      return request<{ sandboxes: AdminSandbox[]; nextCursor: string | null }>(
        `/api/admin/containers/${id}/sandboxes?${params}`,
      );
    },
    killSandbox: (id: string, sandboxId: string) =>
      post<{ success: boolean }>(`/api/admin/containers/${id}/sandboxes/${sandboxId}/kill`, {}),
    pauseSandbox: (id: string, sandboxId: string) =>
      post<{ success: boolean }>(`/api/admin/containers/${id}/sandboxes/${sandboxId}/pause`, {}),
    resumeSandbox: (id: string, sandboxId: string) =>
      post<{ success: boolean }>(`/api/admin/containers/${id}/sandboxes/${sandboxId}/resume`, {}),
    setSandboxTimeout: (id: string, sandboxId: string, timeoutMs: number) =>
      post<{ success: boolean }>(`/api/admin/containers/${id}/sandboxes/${sandboxId}/timeout`, { timeoutMs }),
    vercelSandboxesPage: (id: string, cursor?: string) =>
      request<{ sandboxes: VercelAdminSandbox[]; nextCursor: string | null; access: "team-token" | "brain-env" }>(
        `/api/admin/containers/${id}/vercel-sandboxes${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
      ),
    stopVercelSandbox: (id: string, name: string) =>
      post<{ success: boolean }>(`/api/admin/containers/${id}/vercel-sandboxes/${encodeURIComponent(name)}/stop`, {}),
    deleteVercelSandbox: (id: string, name: string) =>
      post<{ success: boolean }>(`/api/admin/containers/${id}/vercel-sandboxes/${encodeURIComponent(name)}/delete`, {}),
    extendVercelSandbox: (id: string, name: string, durationMs: number) =>
      post<{ success: boolean }>(`/api/admin/containers/${id}/vercel-sandboxes/${encodeURIComponent(name)}/extend`, {
        durationMs,
      }),
    sandboxProvider: (id: string) =>
      request<SandboxProviderStatus>(`/api/admin/containers/${id}/sandbox-provider`),
    // Rewrites the brain's Railway env; Railway redeploys it.
    switchSandboxProvider: (id: string, body: SandboxProviderSwitch) =>
      post<SandboxProviderSwitchResult>(`/api/admin/containers/${id}/sandbox-provider`, body),
    teardownPreflight: (id: string) =>
      request<TeardownPreflight>(
        `/api/admin/containers/${id}/teardown-preflight`,
      ),
    reprovisionCustomDomain: (id: string) =>
      post<{ id: string; status: string }>(
        `/api/admin/containers/${id}/custom-domain/reprovision`,
        {},
      ),
    deployBrainImage: (id: string, tag: string) =>
      post<{
        ok: boolean;
        id: string;
        name: string;
        image: string;
        deploymentId: string;
        releaseChannel: ReleaseChannel;
      }>(`/api/admin/containers/${id}/deploy-image`, { tag }),
    setReleaseChannel: (id: string, channel: ReleaseChannel) =>
      patch<{ id: string; releaseChannel: ReleaseChannel }>(
        `/api/admin/containers/${id}/release-channel`,
        { channel },
      ),
    brainVersions: () =>
      request<{ versions: BrainVersion[] }>("/api/admin/brain-versions"),
    containerImage: (id: string) =>
      request<ContainerImage>(`/api/admin/containers/${id}/image`),
    containerModels: (id: string) =>
      request<ModelSettings>(`/api/admin/containers/${id}/models`),
    containerModelCatalog: (id: string) =>
      request<ModelCatalog>(`/api/admin/containers/${id}/models/catalog`),
    // actor is stamped server-side from the session; nothing to pass here.
    setContainerModels: (id: string, models: ModelSettingsUpdate) =>
      put<ModelSettingsUpdateResult>(`/api/admin/containers/${id}/models`, {
        models,
      }),
    modelPolicy: (orgId: string) => request<AdminModelPolicy>(`/api/admin/model-policy/${orgId}`),
    // Runtime feedback packages (super-admin only — they carry customer
    // conversations). One Boo at a time: its list as summary rows (open
    // reports unless `state` says otherwise), one manifest, its tar.gz
    // archive, and the archive/reopen verdict.
    containerFeedbackSummary: (
      containerId: string,
      opts: { state?: FeedbackListState; limit?: number; cursor?: string } = {},
    ) =>
      request<AdminFeedbackSummary>(
        `/api/admin/containers/${containerId}/feedback?summary=1&state=${opts.state ?? "open"}${
          opts.limit ? `&limit=${opts.limit}` : ""
        }${opts.cursor ? `&cursor=${encodeURIComponent(opts.cursor)}` : ""}`,
      ),
    feedbackManifest: (containerId: string, reportId: string) =>
      request<FeedbackManifest>(
        `/api/admin/containers/${containerId}/feedback/${reportId}`,
      ),
    downloadFeedbackArchive: (containerId: string, reportId: string) =>
      requestBlob(
        `/api/admin/containers/${containerId}/feedback/${reportId}/archive`,
      ),
    // Answers the updated manifest. `by` is stamped server-side from the session.
    setFeedbackReview: (containerId: string, reportId: string, update: FeedbackReviewUpdate) =>
      put<FeedbackManifest>(
        `/api/admin/containers/${containerId}/feedback/${reportId}/review`,
        update,
      ),
    llmUsage: (period: CostPeriod) =>
      request<LLMUsageOverview>(`/api/admin/llm-usage?period=${period}`),
    computeSyncStatus: () =>
      request<ComputeSyncHealth>(
        "/api/admin/infrastructure/compute-sync-status",
      ),
    syncCompute: () =>
      post<ComputeSyncResult>("/api/admin/infrastructure/compute-sync", {}),
    gchatCustomInstalled: () =>
      request<AdminGchatCustomInstalledResponse>(
        "/api/admin/gchat/custom/installed",
      ),
    slackInstalled: () =>
      request<AdminSlackInstalledResponse>("/api/admin/slack/installed"),
    namedWarmPool: () =>
      request<{ data: AdminNamedWarmPoolRow[] }>("/api/admin/named-warm-pool"),
    createNamedWarmPoolEntry: (body: CreateNamedWarmPoolBody) =>
      post<{
        id: string;
        subdomain: string;
        companyName: string;
        domains: string[];
      }>("/api/admin/named-warm-pool", body),
    provisionNamedWarmPoolEntry: (id: string) =>
      post<{ accepted: boolean; id: string }>(
        `/api/admin/named-warm-pool/${id}/provision`,
        {},
      ),
    namedWarmPoolDomains: (id: string) =>
      request<{ domains: string[] }>(
        `/api/admin/named-warm-pool/${id}/domains`,
      ),
    deleteNamedWarmPoolEntry: (id: string) =>
      del<{ success: boolean; tornDown: boolean; warning?: string }>(
        `/api/admin/named-warm-pool/${id}`,
      ),
    changeNamedWarmPoolSubdomain: (id: string, subdomain: string) =>
      post<{ id: string; subdomain: string }>(
        `/api/admin/named-warm-pool/${id}/subdomain`,
        { subdomain },
      ),
    // Trigger the brand-skill build for a parked entry's brain (pre-handoff).
    brandBuildNamedWarmPoolEntry: (id: string, website: string) =>
      post<{ status: string; containerId: string; website: string }>(
        `/api/admin/named-warm-pool/${id}/brand-build`,
        { website },
      ),
    // Automation API keys.
    agentKeyCapabilities: () =>
      request<{
        data: { categories: AgentKeyCapabilityCategory[] };
        _permissions: Permissions;
      }>("/api/admin/agent-keys/capabilities"),
    agentKeys: () =>
      request<{ data: AgentKeyRow[]; _permissions: Permissions }>(
        "/api/admin/agent-keys",
      ),
    createAgentKey: (body: {
      label: string;
      capabilities: string[];
      expiresAt?: string | null;
    }) => post<{ data: CreateAgentKeyResponse }>("/api/admin/agent-keys", body),
    // A Boo that runs on the org's own machine: create it (the token comes
    // back once), make a new token, and set the URL Boo Lab reaches it at.
    selfHosted: {
      create: (orgId: string, body: { name: string; ownerUserId: string; brainUrl?: string | null }) =>
        post<{ data: SelfHostedBooToken }>(`/api/admin/organizations/${orgId}/self-hosted`, body),
      rotateToken: (containerId: string) =>
        post<{ data: SelfHostedBooToken }>(`/api/admin/containers/${containerId}/self-hosted/token`, {}),
      setUrl: (containerId: string, brainUrl: string | null) =>
        patch<{ data: SelfHostedBoo }>(`/api/admin/containers/${containerId}/self-hosted`, { brainUrl }),
    },
    editAgentKey: (id: string, capabilities: string[]) =>
      patch<{ data: AgentKeyRow }>(`/api/admin/agent-keys/${id}`, {
        capabilities,
      }),
    revokeAgentKey: (id: string) =>
      post<{ data: AgentKeyRow }>(`/api/admin/agent-keys/${id}/revoke`, {}),
    agentKeyUsageGuide: (id: string) =>
      request<{ data: { usageGuide: string } }>(
        `/api/admin/agent-keys/${id}/usage-guide`,
      ),
    // The Railway token a Boo deploys its bundle apps with (any live Boo):
    // what the Boo holds, and a replacement that deletes every other token.
    bundleRailwayToken: {
      check: (containerId: string) =>
        request<{ data: BundleRailwayTokenReport }>(
          `/api/admin/containers/${containerId}/bundle-railway-token`,
        ),
      rotate: (containerId: string) =>
        post<{ data: BundleRailwayTokenRotation }>(
          `/api/admin/containers/${containerId}/bundle-railway-token/rotate`,
        ),
    },
    // Admin-scoped bundle management on a PARKED pool brain by id. Mirrors
    // api.containers.bundles but hits /api/admin/* (site-admin gated); the
    // backend scopes these to the parked org and rejects claimed brains.
    containerBundles: {
      list: (containerId: string) =>
        request<BundleListResponse>(
          `/api/admin/containers/${containerId}/bundles`,
        ),
      config: (containerId: string) =>
        request<{ data: BundleConfig; _permissions: Permissions }>(
          `/api/admin/containers/${containerId}/bundles/config`,
        ),
      configure: (containerId: string) =>
        post<{ status: BundleConfigStatus }>(
          `/api/admin/containers/${containerId}/bundles/configure`,
        ),
      attachDomains: (containerId: string) =>
        post<{ status: "attaching"; alreadyRunning?: boolean; bundles?: string[] }>(
          `/api/admin/containers/${containerId}/bundles/attach-domains`,
        ),
      // Admin installs take no source choice (parked brains have no GitHub).
      install: (containerId: string, bundle: string, opts: Omit<BundleInstallOptions, "source" | "repository"> = {}) =>
        post<{ bundle: string; status: BundleInstallStatus }>(
          `/api/admin/containers/${containerId}/bundles/${encodeURIComponent(bundle)}/install`,
          opts,
        ),
      upgrade: (containerId: string, bundle: string, inputs?: Record<string, string>) =>
        post<{ bundle: string; status: BundleInstallStatus | "automatic"; target?: string }>(
          `/api/admin/containers/${containerId}/bundles/${encodeURIComponent(bundle)}/upgrade`,
          inputs ? { inputs } : {},
        ),
      uninstall: (containerId: string, bundle: string) =>
        post<{ bundle: string; status: BundleInstallStatus }>(
          `/api/admin/containers/${containerId}/bundles/${encodeURIComponent(bundle)}/uninstall`,
        ),
      retry: (containerId: string, bundle: string) =>
        post<{ bundle: string; status: "retrying" }>(
          `/api/admin/containers/${containerId}/bundles/${encodeURIComponent(bundle)}/retry`,
        ),
      deployPlatforms: (containerId: string) =>
        request<{ data: { platforms: DeployPlatform[] }; _permissions: Permissions }>(
          `/api/admin/containers/${containerId}/bundles/deploy-platforms`,
        ),
      configureGcpPlatform: (
        containerId: string,
        body: {
          serviceAccount: string;
          projectId: string;
          region: string;
          iapDomain: string;
        },
      ) =>
        post<{ status: string }>(
          `/api/admin/containers/${containerId}/bundles/deploy-platforms/gcp`,
          body,
        ),
    },
    subdomains: {
      list: () =>
        request<{ data: AdminSubdomainsListRow[] }>("/api/admin/subdomains"),
      get: (orgId: string) =>
        request<{ data: AdminOrgSubdomain }>(`/api/admin/subdomains/${orgId}`),
      update: (orgId: string, subdomain: string) =>
        patch<{ data: AdminSubdomainPatchResult }>(
          `/api/admin/subdomains/${orgId}`,
          { subdomain },
        ),
    },
    orgBilling: (orgId: string) =>
      request<AdminOrgBillingResponse>(
        `/api/admin/organizations/${orgId}/billing`,
      ),
    orgLedger: (
      orgId: string,
      limit: number,
      offset: number,
      types?: string[],
    ) => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      if (types?.length) params.set("type", types.join(","));
      return request<AdminLedgerPage>(
        `/api/admin/organizations/${orgId}/ledger?${params}`,
      );
    },
    orgPrepaid: (orgId: string, limit: number, offset: number) =>
      request<AdminOrgPrepaid>(
        `/api/admin/organizations/${orgId}/billing/prepaid?limit=${limit}&offset=${offset}`,
      ),
    orgCreditsTopup: (
      orgId: string,
      amount: number,
      description?: string,
      grantLabel?: string,
    ) =>
      post<{
        organizationId: string;
        amountGranted: number;
        newBalance: number;
      }>(`/api/admin/organizations/${orgId}/credits/topup`, {
        amount,
        description,
        grantLabel,
      }),
    // Signed balance correction (can be negative); reason is mandatory.
    orgCreditsAdjust: (orgId: string, amount: number, reason: string) =>
      post<{ organizationId: string; amount: number; newBalance: number }>(
        `/api/admin/organizations/${orgId}/credits/adjust`,
        { amount, reason },
      ),
    billingGrants: (limit: number, offset: number) =>
      request<AdminGrantsPage>(
        `/api/admin/billing/grants?limit=${limit}&offset=${offset}`,
      ),
    billingConfig: () =>
      request<{ data: AdminBillingConfigRow[] }>("/api/admin/billing/config"),
    billingConfigUpdate: (key: string, value: string) =>
      put<{ key: string; value: string; updatedAt: string }>(
        `/api/admin/billing/config/${encodeURIComponent(key)}`,
        { value },
      ),
    cronHealth: () => request<AdminCronHealth>("/api/admin/cron-health"),
    orgMarkupOverride: (orgId: string, aiMarkupPercent: number | null) =>
      put<{ organizationId: string; aiMarkupPercent: number | null }>(
        `/api/admin/organizations/${orgId}/markup-override`,
        { aiMarkupPercent },
      ),
    experiments: {
      list: () => request<{ data: ExperimentRow[] }>("/api/admin/experiments"),
      create: (data: {
        key: string;
        description: string;
        active: boolean;
        rolloutPercentage: number;
      }) => post<{ data: ExperimentRow }>("/api/admin/experiments", data),
      update: (
        id: number,
        data: Partial<{
          description: string;
          active: boolean;
          rolloutPercentage: number;
          pinnedContainerIds: string[];
        }>,
      ) => patch<{ data: ExperimentRow }>(`/api/admin/experiments/${id}`, data),
      archive: (id: number) =>
        del<{ success: boolean; id: number }>(`/api/admin/experiments/${id}`),
      resolution: (containerId: string) =>
        request<{ data: ExperimentResolution }>(
          `/api/admin/experiments/resolution/${containerId}`,
        ),
      applied: (containerId: string) =>
        request<{ data: AppliedExperiments }>(
          `/api/admin/experiments/applied/${containerId}`,
        ),
    },
    waitlist: {
      list: (status: WaitlistStatusFilter = "pending") =>
        request<{ data: AdminWaitlistEntry[]; _permissions: Permissions | null }>(
          `/api/admin/waitlist?status=${status}`,
        ),
      settings: () => request<{ enabled: boolean }>("/api/admin/waitlist/settings"),
      setEnabled: (enabled: boolean) =>
        patch<{ enabled: boolean }>("/api/admin/waitlist/settings", { enabled }),
      approve: (id: string) =>
        post<{ ok: boolean; containerId: string | null }>(`/api/admin/waitlist/${id}/approve`, {}),
      addToOrg: (id: string, organizationId: string) =>
        post<{ ok: boolean; organizationId: string }>(`/api/admin/waitlist/${id}/add-to-org`, {
          organizationId,
        }),
      reject: (id: string, reason?: string) =>
        post<{ ok: boolean }>(`/api/admin/waitlist/${id}/reject`, { reason }),
    },
  },
  booMap: {
    // Every link between the org's Boos, from the backend's record.
    get: () => request<{ links: BooMapLink[] }>("/api/boo-map"),
  },
  // The org's Boo access page. Org owners and admins read; org owners act for
  // a Boo's admins, always with a reason the Boo's people see.
  access: {
    overview: () => request<AccessOverview>("/api/org/access"),
    grantable: (booId: string) =>
      request<{ grantable: Grantable; scoped: boolean }>(`/api/org/access/boos/${encodeURIComponent(booId)}/grantable`),
    decide: (
      linkId: string,
      body: { decision: "approve" | "decline"; scopes?: GrantScopes; reshare?: boolean; note?: string | null; reason: string },
    ) => post<{ link: { id: string; status: BooLinkStatus } }>(`/api/org/access/links/${encodeURIComponent(linkId)}/decide`, body),
    changeGrant: (linkId: string, body: { scopes: GrantScopes; reshare?: boolean; reason: string }) =>
      put<{ link: { id: string; status: BooLinkStatus }; ended: boolean; complete: boolean }>(
        `/api/org/access/links/${encodeURIComponent(linkId)}/grant`,
        body,
      ),
    end: (linkId: string, reason: string) =>
      del<{ removed: true; complete: boolean }>(`/api/org/access/links/${encodeURIComponent(linkId)}`, { reason }),
    // Sets up what one Boo gets from another without a request.
    create: (body: { sourceId: string; consumerId: string; scopes: GrantScopes | { preset: GrantPreset }; reshare: boolean; reason: string }) =>
      post<{ link: { id: string; status: BooLinkStatus } }>("/api/org/access/links", body),
    events: (q: AccessLogQuery = {}) =>
      request<{ data: AccessLogEvent[]; nextBefore: string | null }>(`/api/org/access/events${accessLogQuery(q)}`),
    eventsCsv: (q: AccessLogQuery = {}) => requestBlob(`/api/org/access/events.csv${accessLogQuery(q)}`),
    handoverPreview: (booId: string) =>
      request<HandoverPreview>(`/api/org/access/boos/${encodeURIComponent(booId)}/handover`),
    handover: (booId: string, plan: HandoverPlan) =>
      post<{ archived: Array<PersonRef & { name: string | null; archived: number }>; renamedFrom: string | null }>(
        `/api/org/access/boos/${encodeURIComponent(booId)}/handover`,
        plan,
      ),
  },
  containers: {
    list: () => request<ListResponse<ContainerRow>>("/api/containers"),
    // One Boo's audit events (Boo members; the secrets category needs readSecrets).
    audit: {
      list: (containerId: string, q: Omit<AuditQuery, "booId"> = {}) =>
        request<AuditPage>(`/api/containers/${encodeURIComponent(containerId)}/audit${auditQuery(q)}`),
    },
    // Rename a Boo; Boo admins and owners only (canConfigure).
    rename: (id: string, name: string) => patch<ContainerRow>(`/api/containers/${id}`, { name }),
    // Resolve a container's owning org from its id alone — used to redirect
    // org-less deep links (e.g. chat-minted /containers/:id/... URLs) into the
    // correct /$orgId namespace without trusting the viewer's active org.
    resolveOrg: (id: string) =>
      request<{ data: { organizationId: string } }>(
        `/api/containers/${id}/org`,
      ),
    channels: {
      list: (containerId: string) =>
        request<ChannelsResponse>(`/api/containers/${containerId}/channels`),
      platforms: (containerId: string) =>
        request<{ platforms: ChannelPlatformStatus[] }>(
          `/api/containers/${containerId}/channels/platforms`,
        ),
      // refresh=true makes the Boo skip its 5-minute directory cache, so a
      // channel created moments ago shows up.
      directory: (
        containerId: string,
        platform: ChannelPlatform,
        q?: string,
        opts: { refresh?: boolean } = {},
      ) => {
        const search = new URLSearchParams({ platform });
        if (q) search.set("q", q);
        if (opts.refresh) search.set("refresh", "1");
        return request<ChannelDirectoryResponse>(
          `/api/containers/${containerId}/channels/directory?${search.toString()}`,
        );
      },
      meta: (
        containerId: string,
        platform: ChannelPlatform,
        channelOrSpaceId: string,
      ) => {
        const search = new URLSearchParams({ platform, channelOrSpaceId });
        return request<ChannelMetaResponse>(
          `/api/containers/${containerId}/channels/meta?${search.toString()}`,
        );
      },
      // Pre-seed rows from the directory picker — creates the row before the
      // channel/person has ever messaged Boo.
      add: (
        containerId: string,
        channel: {
          platform: string;
          channelOrSpaceId: string;
          mode: ChannelMode;
          surfaceType?: ChannelSurfaceType;
          displayName?: string;
        },
      ) =>
        post<{ ok: boolean }>(
          `/api/containers/${containerId}/channels`,
          channel,
        ),
      addUser: (
        containerId: string,
        user: {
          platform: string;
          providerUserId: string;
          blocked: boolean;
          displayName?: string;
        },
      ) =>
        post<{ ok: boolean }>(
          `/api/containers/${containerId}/channels/users`,
          user,
        ),
      setMode: (
        containerId: string,
        channel: { platform: string; channelOrSpaceId: string },
        mode: ChannelMode,
      ) =>
        patch<{ ok: boolean }>(`/api/containers/${containerId}/channels/mode`, {
          platform: channel.platform,
          channelOrSpaceId: channel.channelOrSpaceId,
          mode,
        }),
      setSurfaceDefault: (
        containerId: string,
        surfaceType: ChannelSurfaceType,
        mode: ChannelMode,
      ) =>
        patch<{ ok: boolean }>(
          `/api/containers/${containerId}/channels/surface-default`,
          { surfaceType, mode },
        ),
      setUserBlocked: (
        containerId: string,
        user: { platform: string; providerUserId: string },
        blocked: boolean,
      ) =>
        patch<{ ok: boolean }>(
          `/api/containers/${containerId}/channels/users/blocked`,
          {
            platform: user.platform,
            providerUserId: user.providerUserId,
            blocked,
          },
        ),
      // Channel-scoped person toggle — writes an explicit per-person override
      // (blocked=true → "block", false → "allow") that beats the channel's
      // allowlist-only default. Only for Boos that report userChannelBlocks:
      // true — older ones would block the person everywhere.
      setUserChannelBlocked: (
        containerId: string,
        user: { platform: string; providerUserId: string },
        channelOrSpaceId: string,
        blocked: boolean,
      ) =>
        patch<{ ok: boolean }>(
          `/api/containers/${containerId}/channels/users/blocked`,
          {
            platform: user.platform,
            providerUserId: user.providerUserId,
            channelOrSpaceId,
            blocked,
          },
        ),
      // Per-channel default: only explicitly allowed people get answers there.
      // Same userChannelBlocks gate as setUserChannelBlocked.
      setChannelAllowlistOnly: (
        containerId: string,
        channel: { platform: string; channelOrSpaceId: string },
        allowlistOnly: boolean,
      ) =>
        patch<{ ok: boolean }>(
          `/api/containers/${containerId}/channels/allowlist-only`,
          {
            platform: channel.platform,
            channelOrSpaceId: channel.channelOrSpaceId,
            allowlistOnly,
          },
        ),
      setUserSpendAccess: (
        containerId: string,
        user: { platform: string; providerUserId: string },
        viewAllSpend: boolean,
        displayName?: string,
      ) =>
        patch<{ ok: boolean }>(
          `/api/containers/${containerId}/channels/users/roles`,
          {
            platform: user.platform,
            providerUserId: user.providerUserId,
            viewAllSpend,
            displayName,
          },
        ),
      setBlockNewUsers: (containerId: string, blockNewUsers: boolean) =>
        patch<{ blockNewUsers: boolean }>(
          `/api/containers/${containerId}/channels/users/default`,
          { blockNewUsers },
        ),
      // null (or empty string) resets to the Boo's built-in text.
      setRejectionMessage: (
        containerId: string,
        rejectionMessage: string | null,
      ) =>
        patch<{ rejectionMessage: string | null }>(
          `/api/containers/${containerId}/channels/rejection-message`,
          { rejectionMessage },
        ),
      // Pre-mode boolean contract — used as the fallback while a Boo predates
      // channel-management v2 (the brain maps these onto modes).
      setResponding: (
        containerId: string,
        channel: { platform: string; channelOrSpaceId: string },
        responding: boolean,
      ) =>
        patch<{ ok: boolean }>(
          `/api/containers/${containerId}/channels/responding`,
          {
            platform: channel.platform,
            channelOrSpaceId: channel.channelOrSpaceId,
            responding,
          },
        ),
      setDefault: (containerId: string, respondByDefault: boolean) =>
        patch<{ respondByDefault: boolean }>(
          `/api/containers/${containerId}/channels/default`,
          { respondByDefault },
        ),
      // The default channel (where Boo posts when nothing names a place).
      // Unrelated to setDefault above, which is the legacy answer-by-default
      // switch. 404 code "not_found" = the platform doesn't know the id; 409
      // code "not_in_channel" = Boo must be invited first (message says where).
      defaultChannel: {
        get: (containerId: string) =>
          request<DefaultChannelResponse>(
            `/api/containers/${containerId}/channels/default-channel`,
          ),
        set: (
          containerId: string,
          target: { platform: ChannelPlatform; channelOrSpaceId: string; displayName?: string },
        ) =>
          put<DefaultChannelResponse>(
            `/api/containers/${containerId}/channels/default-channel`,
            target,
          ),
        clear: (containerId: string) =>
          del<DefaultChannelResponse>(
            `/api/containers/${containerId}/channels/default-channel`,
          ),
      },
      // Only where the platform report says createsChannels. Error codes:
      // missing_scope (workspace connected before Boo could create channels),
      // invalid_name, name_taken (409); anything else carries the platform's
      // own reason as the message.
      create: (
        containerId: string,
        channel: { platform: ChannelPlatform; name: string; isPrivate?: boolean },
      ) =>
        post<CreatedChannel>(`/api/containers/${containerId}/channels/create`, channel),
    },
    // Thread-mode policy (owner/admin only; 409 code "brain_outdated" while
    // the Boo predates the flags routes).
    flags: {
      list: (containerId: string) =>
        request<ThreadFlagsResponse>(`/api/containers/${containerId}/flags`),
      // The brain requires BOTH fields on every write — send the full desired
      // policy, including the current approvers when only access changes.
      setAccess: (
        containerId: string,
        policy: {
          flagId: string;
          access: ThreadFlagAccess;
          approvers: "everyone" | string[];
        },
      ) =>
        patch<{ ok: boolean }>(
          `/api/containers/${containerId}/flags/access`,
          policy,
        ),
    },
    // Boo-to-Boo access. Reads are member+, writes Boo owner/admin. Writes
    // that change what a Boo imports wait for it to restart; `restarted:
    // false` means it was still restarting when the backend stopped waiting.
    connections: {
      list: (containerId: string) =>
        request<ConnectionsResponse>(`/api/containers/${containerId}/connections`),
      // What can be shared, by scope. Without linkId: this Boo's own. With a
      // link this Boo uses: only what the source granted it.
      grantable: (containerId: string, linkId?: string) =>
        request<{ grantable: Grantable; scoped: boolean }>(
          `/api/containers/${containerId}/connections/grantable${linkId ? `?linkId=${encodeURIComponent(linkId)}` : ""}`,
        ),
      // Asks `sourceId` for scopes; granted at once when the caller admins it
      // too. On an approved link it asks for more (kind "more").
      request: (containerId: string, body: { sourceId: string; scopes: GrantScopes | { preset: GrantPreset }; note?: string | null }) =>
        post<{ link: { id: string; status: BooLinkStatus; waiting: boolean }; kind: "request" | "more" | "none" }>(
          `/api/containers/${containerId}/connections/requests`,
          body,
        ),
      // The source's admins answer the waiting request: all, a subset, or no.
      approve: (containerId: string, linkId: string, body: { scopes?: GrantScopes; reshare?: boolean; note?: string | null } = {}) =>
        post<{ link: { id: string; status: BooLinkStatus } }>(
          `/api/containers/${containerId}/connections/links/${encodeURIComponent(linkId)}/approve`,
          body,
        ),
      decline: (containerId: string, linkId: string, body: { note?: string | null } = {}) =>
        post<{ link: { id: string; status: BooLinkStatus } }>(
          `/api/containers/${containerId}/connections/links/${encodeURIComponent(linkId)}/decline`,
          body,
        ),
      // The source's admins change what an approved link grants.
      changeGrant: (containerId: string, linkId: string, body: { scopes: GrantScopes; reshare?: boolean }) =>
        put<{ link: { id: string; status: BooLinkStatus }; ended: boolean; complete: boolean }>(
          `/api/containers/${containerId}/connections/links/${encodeURIComponent(linkId)}/grant`,
          body,
        ),
      // Someone who can't manage this Boo asks its owners and admins.
      askAdmins: (containerId: string, body: { sourceId?: string | null; note?: string | null }) =>
        post<{ sent: boolean; reason?: "already_asked" | "no_admins"; to: string[] }>(
          `/api/containers/${containerId}/connections/ask-admins`,
          body,
        ),
      withdrawMore: (containerId: string, linkId: string) =>
        del<{ ok: true }>(`/api/containers/${containerId}/connections/links/${encodeURIComponent(linkId)}/more`),
      // The consumer's admins choose what it uses, within the grant.
      pick: (containerId: string, linkId: string, scopes: GrantScopes) =>
        put<{ restarted: boolean }>(
          `/api/containers/${containerId}/connections/links/${encodeURIComponent(linkId)}/pick`,
          { scopes },
        ),
      retry: (containerId: string, linkId: string) =>
        post<{ result: "done" | "waiting" | "skipped" }>(
          `/api/containers/${containerId}/connections/links/${encodeURIComponent(linkId)}/retry`,
        ),
      // Withdraws a request or ends a link, from either side.
      end: (containerId: string, linkId: string) =>
        del<{ removed: true; otherSideRemoved: boolean; otherSide: string | null }>(
          `/api/containers/${containerId}/connections/links/${encodeURIComponent(linkId)}`,
        ),
    },
    costsByChannel: (containerId: string) =>
      request<ChannelCostsResponse>(
        `/api/containers/${containerId}/costs/by-channel`,
      ),
    create: (data: {
      name: string;
      description?: string;
      orgInfo?: string;
      orgWebsite?: string;
      agentType?: string;
      posture?: Posture;
      setup?: PostureSetup;
      // Defaults to the creator on the backend.
      ownerUserId?: string;
    }) => post<ContainerRow>("/api/containers", data),
    roles: {
      get: (containerId: string) => request<RolesResponse>(`/api/containers/${containerId}/roles`),
      put: (containerId: string, body: RolesUpdate) =>
        put<RolesResponse>(`/api/containers/${containerId}/roles`, body),
    },
    spendLimits: {
      people: (containerId: string) =>
        request<SpendPeopleResponse>(`/api/containers/${containerId}/spend-limits/people`),
    },
    people: {
      list: (containerId: string) => request<PeopleResponse>(`/api/containers/${containerId}/people`),
      update: (containerId: string, personId: string, body: PersonUpdate) =>
        patch<{ ok: boolean }>(`/api/containers/${containerId}/people/${encodeURIComponent(personId)}`, body),
      link: (containerId: string, personId: string, handle: HandleRef) =>
        post<{ ok: boolean }>(`/api/containers/${containerId}/people/${encodeURIComponent(personId)}/link`, handle),
      addGroup: (containerId: string, personId: string, group: string) =>
        post<{ ok: boolean }>(`/api/containers/${containerId}/people/${encodeURIComponent(personId)}/groups`, { group }),
      removeGroup: (containerId: string, personId: string, group: string) =>
        del<{ ok: boolean }>(
          `/api/containers/${containerId}/people/${encodeURIComponent(personId)}/groups/${encodeURIComponent(group)}`,
        ),
      removeInvite: (containerId: string, invitationId: string) =>
        del<{ ok: boolean }>(`/api/containers/${containerId}/people/invites/${encodeURIComponent(invitationId)}`),
      unlink: (containerId: string, personId: string, handle: HandleRef) =>
        del<{ ok: boolean }>(`/api/containers/${containerId}/people/${encodeURIComponent(personId)}/link`, handle),
    },
    members: {
      list: (containerId: string) =>
        request<BooMembersResponse>(`/api/containers/${containerId}/members`),
      candidates: (containerId: string) =>
        request<{ data: BooMemberCandidate[] }>(`/api/containers/${containerId}/members/candidates`),
      add: (containerId: string, userId: string, role: BooRole) =>
        post<{ ok: boolean }>(`/api/containers/${containerId}/members`, { userId, role }),
      setRole: (containerId: string, userId: string, role: BooRole) =>
        patch<{ ok: boolean }>(`/api/containers/${containerId}/members/${encodeURIComponent(userId)}`, { role }),
      remove: (containerId: string, userId: string) =>
        del<{ ok: boolean }>(`/api/containers/${containerId}/members/${encodeURIComponent(userId)}`),
      // Makes someone an owner. Without userId it is the caller. An org owner
      // who doesn't own the Boo must give a reason; owners and admins are told.
      assignOwner: (containerId: string, data: { userId?: string; reason?: string }) =>
        post<{ ok: boolean; notified?: boolean }>(`/api/containers/${containerId}/members/owners`, data),
      events: (containerId: string) =>
        request<{ data: BooAccessEvent[]; _permissions: BooPermissions }>(
          `/api/containers/${containerId}/members/access-events`,
        ),
      dismissNotice: (containerId: string, eventId: string) =>
        post<{ ok: boolean }>(
          `/api/containers/${containerId}/members/access-events/${encodeURIComponent(eventId)}/dismiss`,
        ),
    },
    start: (id: string) => post<ContainerRow>(`/api/containers/${id}/start`),
    stop: (id: string) => post<ContainerRow>(`/api/containers/${id}/stop`),
    delete: (id: string) => del<{ success: boolean }>(`/api/containers/${id}`),
    logs: (id: string) => request<ContainerLogs>(`/api/containers/${id}/logs`),
    deploymentStatus: (id: string) =>
      request<DeploymentSnapshot>(`/api/containers/${id}/deployment-status`),
    health: (id: string) =>
      request<{ reachable: boolean; state?: "ok" | "starting" | "offline"; lastSeenAt?: string | null }>(`/api/containers/${id}/health`),
    // Two-way dashboard chat with a boo-brain (super-admin only; the backend
    // routes and the dashboard tab are both gated).
    dashboardChat: {
      threads: (containerId: string) =>
        request<{ threads: BrainChatThread[] }>(
          `/api/containers/${containerId}/dashboard-chat/threads`,
        ),
      messages: (containerId: string, threadKey: string) =>
        request<{
          threadId: string;
          threadKey: string;
          messages: BrainChatMessage[];
          // What Boo is doing right now (the per-tool status line), null when idle.
          status?: string | null;
        }>(
          `/api/containers/${containerId}/dashboard-chat/threads/${encodeURIComponent(threadKey)}/messages`,
        ),
      // Stop the turn running on a conversation.
      abort: (containerId: string, threadKey: string) =>
        post<{ threadId: string; wasActive: boolean }>(
          `/api/containers/${containerId}/dashboard-chat/threads/${encodeURIComponent(threadKey)}/abort`,
        ),
      send: (containerId: string, threadKey: string, text: string, opts: { voice?: boolean; files?: File[] } = {}) => {
        if (opts.files && opts.files.length > 0) {
          const form = new FormData();
          form.append("threadKey", threadKey);
          form.append("text", text);
          if (opts.voice) form.append("voice", "true");
          for (const file of opts.files) form.append("files", file, file.name);
          return rawFetch(`/api/containers/${containerId}/dashboard-chat/messages`, { method: "POST", body: form }).then(
            (res) => res.json() as Promise<{ threadId: string; runnerStarted: boolean; status: string }>,
          );
        }
        return post<{ threadId: string; runnerStarted: boolean; status: string }>(
          `/api/containers/${containerId}/dashboard-chat/messages`,
          // voice: the text was spoken and the reply will be read aloud, so
          // the brain answers in speech-friendly prose.
          { threadKey, text, ...(opts.voice ? { voice: true } : {}) },
        );
      },
      // Voice mode, speech in: one recorded utterance → transcript (the brain
      // runs it through its transcription model). 503 = not configured on
      // this Boo.
      transcribe: async (containerId: string, threadKey: string, audio: Blob, filename = "utterance.wav") => {
        const form = new FormData();
        form.append("threadKey", threadKey);
        form.append("file", audio, filename);
        const res = await rawFetch(`/api/containers/${containerId}/dashboard-chat/transcribe`, {
          method: "POST",
          body: form,
        });
        return (await res.json()) as { transcript: string; durationSeconds?: number };
      },
      // Voice mode, speech out: a reply's text → audio (mp3). 503 = platform
      // text-to-speech not configured; callers fall back to the browser's own.
      speech: async (containerId: string, threadKey: string, text: string, opts: { voice?: string } = {}) => {
        const res = await rawFetch(`/api/containers/${containerId}/dashboard-chat/speech`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // voice: a one-off override (the picker's sample); otherwise the
          // Boo's saved voice.
          body: JSON.stringify({ threadKey, text, ...(opts.voice ? { voice: opts.voice } : {}) }),
        });
        return res.blob();
      },
      // Voices the platform's text-to-speech offers this Boo, and which one it
      // uses (null = the service default). configured=false when the platform
      // has no text-to-speech key.
      voices: (containerId: string) =>
        request<{ voices: VoiceOption[]; selected: string | null; configured: boolean }>(
          `/api/containers/${containerId}/dashboard-chat/voices`,
        ),
      setVoice: (containerId: string, voice: string | null) =>
        request<{ selected: string | null }>(`/api/containers/${containerId}/dashboard-chat/voice`, {
          method: "PUT",
          body: JSON.stringify({ voice }),
        }),
      // Decide a pending approval as the signed-in super admin; the brain
      // checks their approval authority for the tools involved.
      decideEscalation: (containerId: string, threadKey: string, escalationId: string, decision: BrainChatDecision) =>
        post<{ actorName: string; approved: number; denied: number; approvedTools: string[]; deniedTools: string[] }>(
          `/api/containers/${containerId}/dashboard-chat/threads/${encodeURIComponent(threadKey)}/escalations/${encodeURIComponent(escalationId)}/decision`,
          { decision },
        ),
      // Marked-up cost (LLM + services) per session, keyed by threadKey.
      costs: (containerId: string) =>
        request<{ costs: Record<string, number> }>(
          `/api/containers/${containerId}/dashboard-chat/costs`,
        ),
    },
    usage: {
      container: (containerId: string) =>
        request<ContainerUsage>(`/api/containers/${containerId}/usage`),
      currentMonth: (containerId: string) =>
        request<ContainerCurrentMonthBilling>(
          `/api/containers/${containerId}/usage/current-month`,
        ),
      byDay: (containerId: string, start?: string, end?: string) =>
        request<ContainerUsageByDay>(
          start && end
            ? `/api/containers/${containerId}/usage/by-day?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
            : `/api/containers/${containerId}/usage/by-day`,
        ),
      /** All-time view: whole Boo life, bucketed by month ('YYYY-MM' keys). */
      byMonthAllTime: (containerId: string) =>
        request<ContainerUsageByDay>(
          `/api/containers/${containerId}/usage/by-day?granularity=month`,
        ),
      // Spend attribution from the Boo's own ledger (boo-brain only). A 409
      // brain_outdated means the Boo predates the report routes.
      report: {
        /** Rollout probe: whether the spend breakdown is on for this Boo
         * (PostHog flag per Boo; platform operators always true). Asked once
         * before the panel renders so an off-rollout Boo shows no trace. */
        access: (containerId: string) =>
          request<{ enabled: boolean }>(
            `/api/containers/${containerId}/usage-report/access`,
          ),
        totals: (
          containerId: string,
          period: UsageReportPeriod,
          filter?: UsageReportFilter,
        ) =>
          request<UsageReportTotalsResponse>(
            `/api/containers/${containerId}/usage-report/totals?${usageReportQuery(period, filter)}`,
          ),
        summary: (
          containerId: string,
          period: UsageReportPeriod,
          groupBy: UsageReportGroup,
          opts?: UsageReportFilter & { limit?: number },
        ) => {
          const search = usageReportQuery(period, opts);
          search.set("groupBy", groupBy);
          if (opts?.limit !== undefined)
            search.set("limit", String(opts.limit));
          return request<UsageReportSummaryResponse>(
            `/api/containers/${containerId}/usage-report/summary?${search.toString()}`,
          );
        },
        series: (
          containerId: string,
          period: UsageReportPeriod,
          opts?: UsageReportFilter & {
            splitBy?: Exclude<UsageReportGroup, "skill">;
          },
        ) => {
          const search = usageReportQuery(period, opts);
          if (opts?.splitBy !== undefined) search.set("splitBy", opts.splitBy);
          return request<UsageSeriesResponse>(
            `/api/containers/${containerId}/usage-report/series?${search.toString()}`,
          );
        },
        sessions: (
          containerId: string,
          period: UsageReportPeriod,
          opts?: UsageReportFilter & {
            limit?: number;
            beforeTs?: number;
            beforeThread?: string;
          },
        ) => {
          const search = usageReportQuery(period, opts);
          if (opts?.limit !== undefined)
            search.set("limit", String(opts.limit));
          if (opts?.beforeTs !== undefined)
            search.set("beforeTs", String(opts.beforeTs));
          if (opts?.beforeThread !== undefined)
            search.set("beforeThread", opts.beforeThread);
          return request<UsageSessionsResponse>(
            `/api/containers/${containerId}/usage-report/sessions?${search.toString()}`,
          );
        },
      },
    },
    // Storage protection on a boo-brain. Any Boo member may read the state;
    // minting the console certificate needs Boo admin (backend-enforced).
    atrestState: (containerId: string) =>
      request<AtrestState>(`/api/containers/${containerId}/atrest-state`),
    // Mints a certificate for the brain's storage console (scope "atrest")
    // and returns the console URL carrying it.
    operatorCertificate: (containerId: string) =>
      post<OperatorCertificate>(`/api/containers/${containerId}/operator-certificate`),
    // Default-vault proxy (boo-brain only). `mount` is the vault mount (the
    // brain agent uses "kv"); `path` is the secret's path within the mount.
    vault: {
      // List secret paths at the mount root, or narrowed to a prefix. The
      // backend auto-adds ?list=true at the root; prefixed listing needs it
      // explicitly. Returns paths only, no values.
      list: (containerId: string, mount: string, prefix?: string) => {
        const base = `/api/containers/${containerId}/vault/default/v1/${encodeURIComponent(mount)}`;
        return request<VaultListResponse>(
          prefix ? `${base}/${encodeVaultPath(prefix)}?list=true` : base,
        );
      },
      // Read a secret's plaintext fields. Owner/admin only (backend-enforced).
      read: (containerId: string, mount: string, path: string) =>
        request<VaultSecret>(
          `/api/containers/${containerId}/vault/default/v1/${encodeURIComponent(mount)}/${encodeVaultPath(path)}`,
        ),
      // Create or replace a secret's data (PUT replaces the whole data object).
      write: (
        containerId: string,
        mount: string,
        path: string,
        data: Record<string, string>,
      ) =>
        put<VaultSecret>(
          `/api/containers/${containerId}/vault/default/v1/${encodeURIComponent(mount)}/${encodeVaultPath(path)}`,
          { data },
        ),
      // Delete a secret. Owner/admin only.
      remove: (containerId: string, mount: string, path: string) =>
        del<void>(
          `/api/containers/${containerId}/vault/default/v1/${encodeURIComponent(mount)}/${encodeVaultPath(path)}`,
        ),
    },
    // Installable add-ons that run on this brain. Per-container: every route is
    // scoped to the brain's :id. List/config require a running brain (409
    // brain_not_ready / 404 not_found otherwise). Mutations return 202 and run
    // in the background: poll list/config until a terminal status.
    bundles: {
      list: (containerId: string) =>
        request<BundleListResponse>(
          `/api/containers/${containerId}/bundles`,
        ),
      // A Boo that reconciles its bundles (`reconciler: 1`) runs this one
      // again: Retry after a failure, Repair when it isn't running.
      // 409 bundle_not_installed for a bundle the Boo doesn't track,
      // retry_unavailable for a Boo that doesn't reconcile.
      retry: (containerId: string, bundle: string) =>
        post<{ bundle: string; status: "retrying" }>(
          `/api/containers/${containerId}/bundles/${encodeURIComponent(bundle)}/retry`,
        ),
      config: (containerId: string) =>
        request<{ data: BundleConfig; _permissions: Permissions }>(
          `/api/containers/${containerId}/bundles/config`,
        ),
      configure: (containerId: string) =>
        post<{ status: BundleConfigStatus }>(
          `/api/containers/${containerId}/bundles/configure`,
        ),
      // Re-run the branded-domain attach for apps still on their Railway
      // service domain (install.domainAttached === false).
      // alreadyRunning: an attach was already under way; poll the list either way.
      attachDomains: (containerId: string) =>
        post<{ status: "attaching"; alreadyRunning?: boolean; bundles?: string[] }>(
          `/api/containers/${containerId}/bundles/attach-domains`,
        ),
      // `bundle` is the catalog id (e.g. "boosecret"), not the slug.
      install: (containerId: string, bundle: string, opts: BundleInstallOptions = {}) =>
        post<{ bundle: string; status: BundleInstallStatus }>(
          `/api/containers/${containerId}/bundles/${encodeURIComponent(bundle)}/install`,
          opts,
        ),
      // Runs in the background (minutes for an app); poll list for the result.
      // `inputs` fills required inputs the new version added (install.missingInputs).
      upgrade: (containerId: string, bundle: string, inputs?: Record<string, string>) =>
        post<{ bundle: string; status: BundleInstallStatus | "automatic"; target?: string }>(
          `/api/containers/${containerId}/bundles/${encodeURIComponent(bundle)}/upgrade`,
          inputs ? { inputs } : {},
        ),
      uninstall: (containerId: string, bundle: string) =>
        post<{ bundle: string; status: BundleInstallStatus }>(
          `/api/containers/${containerId}/bundles/${encodeURIComponent(bundle)}/uninstall`,
        ),
      deployPlatforms: (containerId: string) =>
        request<{ data: { platforms: DeployPlatform[] }; _permissions: Permissions }>(
          `/api/containers/${containerId}/bundles/deploy-platforms`,
        ),
      configureGcpPlatform: (
        containerId: string,
        body: {
          serviceAccount: string;
          projectId: string;
          region: string;
          iapDomain: string;
        },
      ) =>
        post<{ status: string }>(
          `/api/containers/${containerId}/bundles/deploy-platforms/gcp`,
          body,
        ),
    },
  },
  onboarding: {
    state: () => request<OnboardingState>("/api/onboarding/state"),
    complete: () => post<{ ok: boolean }>("/api/onboarding/complete"),
    // Sends Boo's held Slack greeting once the default channel is decided.
    kickoff: () => post<{ ok: boolean; released: boolean }>("/api/onboarding/kickoff"),
    suggestions: (website?: string) => {
      const params = website ? `?website=${encodeURIComponent(website)}` : "";
      return request<OnboardingSuggestions>(
        `/api/onboarding/suggestions${params}`,
      );
    },
    orgStepComplete: (data: {
      orgName: string;
      orgInfo?: string;
      agentName: string;
      agentType?: string;
    }) => post<{ ok: boolean }>("/api/onboarding/org-step-complete", data),
    nameUpdated: (name: string) =>
      post<{ ok: boolean }>("/api/onboarding/name-updated", { name }),
  },
  usage: {
    org: () => request<OrgUsage>("/api/usage"),
    spendByDay: (start: string, end: string) =>
      request<OrgSpendByDay>(
        `/api/usage/spend-by-day?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
      ),
    /** All-time view: whole ledger, bucketed by month ('YYYY-MM' day keys). */
    spendByMonthAllTime: () =>
      request<OrgSpendByDay>("/api/usage/spend-by-day?granularity=month"),
  },
  billing: {
    overview: () => request<BillingOverview>("/api/billing/overview"),
    credits: () => request<CreditInfo>("/api/billing/credits"),
    currentUsage: () => request<CurrentUsage>("/api/billing/usage/current"),
    // Prepaid credits (BOO-331). checkout/setup return a hosted-checkout URL
    // to redirect to (mock page until Stripe lands).
    checkout: (amountUsd: number) =>
      post<{ checkoutUrl: string; sessionId: string }>(
        "/api/billing/checkout",
        { amountUsd },
      ),
    setupPaymentMethod: () =>
      post<{ checkoutUrl: string; sessionId: string }>(
        "/api/billing/payment-method/setup",
      ),
    details: () => request<BillingDetails>("/api/billing/details"),
    updateDetails: (body: { name: string | null; address: BillingAddress | null }) =>
      put<BillingDetails>("/api/billing/details", body),
    autoRecharge: () =>
      request<AutoRechargeConfig>("/api/billing/auto-recharge"),
    updateAutoRecharge: (cfg: {
      enabled: boolean;
      thresholdUsd: number;
      amountUsd: number;
      monthlyCapUsd: number | null;
    }) =>
      put<{
        enabled: boolean;
        thresholdUsd: number;
        amountUsd: number;
        monthlyCapUsd: number | null;
      }>("/api/billing/auto-recharge", cfg),
    /** Only the reset timezone is written here now (deprecated endpoint). */
    updateSpendTimezone: (timezone: string) =>
      put<SpendCapsResponse>("/api/billing/spend-caps", { timezone }),
    spendLimits: {
      get: () => request<SpendLimitsPage>("/api/billing/spend-limits"),
      setLimit: (body: SpendLimitInput) =>
        put<SpendRow>("/api/billing/spend-limits/limit", body),
      removePersonLimit: (personKey: string) =>
        del<{ ok: boolean }>("/api/billing/spend-limits/limit", {
          target: { scope: "person", personKey },
        }),
      /** One-time extra for the current day or month, without changing the limit. */
      addExtra: (body: SpendGrantInput) =>
        post<{ extra: SpendExtra; row: SpendRow | null }>("/api/billing/spend-limits/extra", body),
      /** Removes an unused extra; `409 extra_in_use` once spend relies on it. */
      removeExtra: (id: string) =>
        del<{ ok: boolean }>(`/api/billing/spend-limits/extra/${encodeURIComponent(id)}`),
      /** `ownLevels`: limits with their own warn % that the change leaves alone. */
      setDefaultWarn: (warnPercent: number) =>
        put<{ warnPercent: number; ownLevels?: { name: string; warnPercent: number }[] }>("/api/billing/spend-limits/default-warn", { warnPercent }),
      /** 201 with the new group's row. */
      createGroup: (body: SpendGroupInput) =>
        post<SpendGroupRow>("/api/billing/spend-limits/groups", body),
      renameGroup: (groupId: string, name: string) =>
        patch<SpendGroupRow>(`/api/billing/spend-limits/groups/${encodeURIComponent(groupId)}`, { name }),
      deleteGroup: (groupId: string) =>
        del<{ ok: boolean }>(`/api/billing/spend-limits/groups/${encodeURIComponent(groupId)}`),
      addMember: (groupId: string, personKey: string, move?: boolean) =>
        put<SpendGroupRow>(
          `/api/billing/spend-limits/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(personKey)}`,
          move ? { move: true } : {},
        ),
      removeMember: (groupId: string, personKey: string) =>
        del<SpendGroupRow>(
          `/api/billing/spend-limits/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(personKey)}`,
        ),
    },
    transactions: (limit: number, offset: number, types?: string[]) => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      if (types?.length) params.set("type", types.join(","));
      return request<TransactionsPage>(`/api/billing/transactions?${params}`);
    },
    mockSession: (id: string) =>
      request<MockSession>(`/api/billing/mock-sessions/${id}`),
    mockPay: (id: string) =>
      post<{ redirectUrl: string }>(`/api/billing/mock-sessions/${id}/pay`),
    mockCancel: (id: string) =>
      post<{ redirectUrl: string }>(`/api/billing/mock-sessions/${id}/cancel`),
  },
  apps: {
    list: () => request<ListResponse<AppRow>>("/api/apps"),
    create: (data: { name: string; description?: string }) =>
      post<AppRow>("/api/apps", data),
    update: (id: string, data: Partial<Pick<AppRow, "name" | "description">>) =>
      patch<AppRow>(`/api/apps/${id}`, data),
    delete: (id: string) => del<{ success: boolean }>(`/api/apps/${id}`),
  },
  // Integrations Hub — session-cookie authed against the active org (no org id
  // in path). Everything goes through boo-backend; the browser holds no hub
  // credential. Connecting is a redirect: connectLink() → leave for `url` →
  // the hub's hosted page → back to returnTo with ?integrations=<outcome>.
  integrations: {
    // Hub catalog, cursor-paged. `category` filters to it and its descendants;
    // `connectedBy: "hub"` hides apps boo-backend connects itself.
    apps: (params: {
      q?: string;
      category?: string;
      connectedBy?: "hub" | "app";
      cursor?: string;
      limit?: number;
    } = {}) => {
      const search = new URLSearchParams();
      if (params.q) search.set("q", params.q);
      if (params.category) search.set("category", params.category);
      if (params.connectedBy) search.set("connectedBy", params.connectedBy);
      if (params.cursor) search.set("cursor", params.cursor);
      if (params.limit) search.set("limit", String(params.limit));
      const qs = search.toString();
      return request<AppsPage>(`/api/integrations/apps${qs ? `?${qs}` : ""}`);
    },
    // Category taxonomy — flat list, nest via parentSlug.
    categories: () =>
      request<{ categories: CategoryInfo[] }>("/api/integrations/categories").then((r) => r.categories),
    // POST /connect-links → a one-time URL to leave for. With containerId:
    // anyone who can configure that Boo; the account becomes private to it.
    // Without: org owner/admin; the account is workspace-scoped. The backend
    // binds for that scope on completion and redirects to returnTo (a
    // dashboard path) with ?integrations=connected|cancelled|error.
    connectLink: (body: { app: string; containerId?: string | null; returnTo?: string }) =>
      post<{ url: string; expiresIn: number; containerId: string | null }>(
        "/api/integrations/connect-links",
        body,
      ),
    // GET /accounts → workspace accounts, plus (with containerId, which needs
    // view on that Boo) the accounts private to it.
    accounts: (containerId?: string) =>
      request<{
        accounts: HubAccountWithBindings[];
        _permissions: Permissions;
      }>(
        `/api/integrations/accounts${containerId ? `?containerId=${encodeURIComponent(containerId)}` : ""}`,
      ),
    // Bind an account to one Boo (containerId; configure on it) or org-wide
    // (null/omitted; org owner/admin, workspace accounts only). A Boo-private
    // account binds only to its home (403 account_private otherwise).
    bind: (body: {
      hubAccountId: string;
      containerId?: string | null;
      purpose?: string | null;
      toolAccess?: ToolAccess;
    }) =>
      post<{
        binding: IntegrationBinding;
        account: { hubAccountId: string; scope: HubAccountScope; homeContainerId: string | null };
      }>("/api/integrations/bindings", body),
    updateBinding: (
      id: string,
      body: { toolAccess?: ToolAccess; purpose?: string | null },
    ) =>
      patch<{ binding: IntegrationBinding }>(
        `/api/integrations/bindings/${encodeURIComponent(id)}`,
        body,
      ),
    unbind: (id: string) =>
      del<{ ok: boolean }>(
        `/api/integrations/bindings/${encodeURIComponent(id)}`,
      ),
    // Private → workspace: every Boo may be bound. Home-Boo configure.
    share: (accountId: string) =>
      post<{ account: { hubAccountId: string; scope: HubAccountScope; homeContainerId: string | null } }>(
        `/api/integrations/accounts/${encodeURIComponent(accountId)}/share`,
      ),
    // Workspace → private again; bindings outside the home Boo are dropped.
    // 409 no_home_boo when the home Boo is gone.
    unshare: (accountId: string) =>
      post<{
        account: { hubAccountId: string; scope: HubAccountScope; homeContainerId: string | null };
        removedBindings: number;
      }>(`/api/integrations/accounts/${encodeURIComponent(accountId)}/unshare`),
    // Disconnect at the hub (revokes upstream where supported) + drop bindings.
    disconnect: (accountId: string) =>
      del<{ ok: boolean }>(
        `/api/integrations/accounts/${encodeURIComponent(accountId)}`,
      ),
    // Every tool an app exposes — for the manage panel's per-tool toggles.
    appTools: (slug: string) =>
      request<{ tools: HubTool[] }>(
        `/api/integrations/apps/${encodeURIComponent(slug)}/tools`,
      ).then((r) => r.tools),
  },
  connectors: (() => {
    const base = (containerId: string, slug?: string) =>
      `/api/containers/${encodeURIComponent(containerId)}/connectors${slug ? `/${encodeURIComponent(slug)}` : ""}`;
    return {
      list: (containerId: string) =>
        request<{ connectors: ConnectorSummary[]; _permissions?: { canConfigure: boolean } }>(base(containerId)),
      get: (containerId: string, slug: string) => request<ConnectorDetail>(base(containerId, slug)),
      // A registered commit, diffed against the running version.
      review: (containerId: string, slug: string, commit: string) =>
        request<ConnectorReview>(`${base(containerId, slug)}/review/${encodeURIComponent(commit)}`),
      // Shown to Boo, which answers in the thread and may register a new commit.
      comment: (containerId: string, slug: string, commit: string, body: { body: string; path?: string; line?: number }) =>
        post<{ comment: ConnectorComment }>(`${base(containerId, slug)}/review/${encodeURIComponent(commit)}/comments`, body),
      // Boo-hosted: approves exactly this commit and it goes live.
      approve: (containerId: string, slug: string, commit: string) =>
        post<{ status: ConnectorStatus; runningCommit?: string }>(
          `${base(containerId, slug)}/review/${encodeURIComponent(commit)}/approve`,
        ),
      // Code host: the same route approves nothing; it re-checks the default
      // branch. 200 live with whatever runs now (runningCommit may differ from
      // commit), or 409 { code, error, main }.
      checkHost: (containerId: string, slug: string, commit: string) =>
        post<{ status: ConnectorStatus; runningCommit?: string; main?: ConnectorMain | null }>(
          `${base(containerId, slug)}/review/${encodeURIComponent(commit)}/approve`,
        ),
      saveVariables: (containerId: string, slug: string, values: Record<string, string>) =>
        putConnectorVariables(`${base(containerId, slug)}/variables`, values),
      // Disconnect: deletes every stored value; the connector turns disabled.
      disconnect: (containerId: string, slug: string) =>
        del<{ ok: boolean; rotateNotice?: string }>(`${base(containerId, slug)}/connection`),
      verify: (containerId: string, slug: string) =>
        post<ConnectorVerifyResult>(`${base(containerId, slug)}/verify`),
      setActionStates: (containerId: string, slug: string, states: Record<string, ToolState>) =>
        patch<{ actions: ConnectorAction[] }>(`${base(containerId, slug)}/actions`, { states }),
      versions: (containerId: string, slug: string) =>
        request<{ versions: ConnectorVersion[] }>(`${base(containerId, slug)}/versions`).then((r) => r.versions),
      // A version against the one approved before it.
      versionDiff: (containerId: string, slug: string, commit: string) =>
        request<Pick<ConnectorReview, "files" | "versionVariables" | "baseVariables">>(
          `${base(containerId, slug)}/versions/${encodeURIComponent(commit)}/diff`,
        ),
      // connected false: that version's variables have to be filled in again.
      rollback: (containerId: string, slug: string, commit: string) =>
        post<{ status: ConnectorStatus; runningCommit: string; connected?: boolean }>(`${base(containerId, slug)}/rollback`, { commit }),
      activity: (containerId: string, slug: string, cursor?: string) =>
        request<{ items: ConnectorActivity[]; nextCursor?: string | null }>(
          `${base(containerId, slug)}/activity${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
        ),
      // Boo-hosted: a personal token for the session user, shown once. push
      // needs configure and lands on a review branch.
      cloneToken: (containerId: string, slug: string, access: "clone" | "push") =>
        post<ConnectorCloneToken>(`${base(containerId, slug)}/clone-token`, { access }),
      // Every file of any registered version, read-only.
      files: (containerId: string, slug: string, commit: string) =>
        request<{ commit: string; files: ConnectorFile[] }>(`${base(containerId, slug)}/files/${encodeURIComponent(commit)}`),
    };
  })(),
  // One-time link, no session.
  connectorLink: {
    get: (token: string) => request<ConnectorLinkView>(`/api/connector-link/${encodeURIComponent(token)}`),
    saveVariables: (token: string, values: Record<string, string>) =>
      putConnectorVariables(`/api/connector-link/${encodeURIComponent(token)}/variables`, values),
  },
  // The connection form for someone with no role in the Boo (outside the Boo
  // access guard). 403 setup_not_allowed when the org lets only Boo admins
  // set up connectors.
  connectorSetup: (() => {
    const base = (containerId: string, slug: string) =>
      `/api/connector-setup/${encodeURIComponent(containerId)}/${encodeURIComponent(slug)}`;
    return {
      get: (containerId: string, slug: string) => request<ConnectorSetupView>(base(containerId, slug)),
      saveVariables: (containerId: string, slug: string, values: Record<string, string>) =>
        putConnectorVariables(`${base(containerId, slug)}/variables`, values),
    };
  })(),
  slack: {
    boo: {
      status: () => request<BooSlackStatus>("/api/slack/boo/status"),
      install: (containerId?: string, returnTo?: string) => {
        const params = new URLSearchParams();
        if (containerId) params.set("containerId", containerId);
        if (returnTo) params.set("returnTo", returnTo);
        const qs = params.toString();
        return request<InstallUrlResponse>(
          `/api/slack/boo/install${qs ? `?${qs}` : ""}`,
        );
      },
      disconnect: () => del<{ success: boolean }>("/api/slack/boo/install"),
      move: (containerId: string) =>
        post<{ success: boolean }>("/api/slack/boo/move", { containerId }),
      // The owner of the Boo the workspace answers through lets it go, so an
      // admin of another Boo can attach it.
      release: () => post<{ success: boolean }>("/api/slack/boo/release"),
    },
    custom: {
      // sync=true reads the live app name from Slack (apps.manifest.export) so a
      // rename done directly in Slack is reflected. Only pass it on mount /
      // manual refresh — never on a background poll.
      status: (containerId: string, sync?: boolean) =>
        request<SlackStatus>(
          `/api/slack/custom/install/${containerId}${sync ? "?sync=1" : ""}`,
        ),
      install: (containerId: string, botName?: string, returnTo?: string) => {
        const params = new URLSearchParams({ containerId });
        if (botName) params.set("botName", botName);
        if (returnTo) params.set("returnTo", returnTo);
        return request<InstallUrlResponse>(
          `/api/slack/custom/install?${params}`,
        );
      },
      rename: (containerId: string, botName: string) =>
        patch<{ ok: boolean; botName: string; _permissions: Permissions }>(
          `/api/slack/custom/install/${containerId}`,
          { botName },
        ),
      disconnect: (containerId: string) =>
        del<{ success: boolean }>(`/api/slack/custom/install/${containerId}`),
      workspace: {
        status: () =>
          request<SlackWorkspaceStatus>("/api/slack/custom/workspace-token"),
        save: (refreshToken: string) =>
          post<{ connected: boolean; teamId: string; teamName: string | null }>(
            "/api/slack/custom/workspace-token",
            { refreshToken },
          ),
        clear: () => del<{ ok: boolean }>("/api/slack/custom/workspace-token"),
      },
    },
  },
  teams: {
    boo: {
      status: () => request<BooTeamsStatus>("/api/teams/boo/status"),
      install: (containerId?: string, returnTo?: string) => {
        const params = new URLSearchParams();
        if (containerId) params.set("containerId", containerId);
        if (returnTo) params.set("returnTo", returnTo);
        const qs = params.toString();
        return request<InstallUrlResponse>(
          `/api/teams/boo/install${qs ? `?${qs}` : ""}`,
        );
      },
      disconnect: () => del<{ success: boolean }>("/api/teams/boo/install"),
      move: (containerId: string) =>
        post<{ success: boolean }>("/api/teams/boo/move", { containerId }),
      // The owner of the Boo the workspace answers through lets it go, so an
      // admin of another Boo can attach it.
      release: () => post<{ success: boolean }>("/api/teams/boo/release"),
    },
  },
  discord: {
    boo: {
      status: () => request<BooDiscordStatus>("/api/discord/boo/status"),
      install: (containerId?: string, returnTo?: string) => {
        const params = new URLSearchParams();
        if (containerId) params.set("containerId", containerId);
        if (returnTo) params.set("returnTo", returnTo);
        const qs = params.toString();
        return request<InstallUrlResponse>(
          `/api/discord/boo/install${qs ? `?${qs}` : ""}`,
        );
      },
      disconnect: () => del<{ ok: boolean }>("/api/discord/boo/install"),
      move: (containerId: string) =>
        post<{ ok: boolean }>("/api/discord/boo/move", { containerId }),
      release: () => post<{ success: boolean }>("/api/discord/boo/release"),
    },
  },
  ms365: {
    status: (containerId: string) =>
      request<Ms365Status>(
        `/api/ms365/status?containerId=${encodeURIComponent(containerId)}`,
      ),
    // Admin grants org-wide consent once (mints no token); then the boo@
    // account signs in. Both take the selected capability scopes and return a
    // Microsoft URL to redirect the browser to.
    adminConsent: (containerId: string, scopes: string, returnTo?: string) => {
      const params = new URLSearchParams({ containerId });
      if (scopes) params.set("scopes", scopes);
      if (returnTo) params.set("returnTo", returnTo);
      return request<InstallUrlResponse>(`/api/ms365/admin-consent?${params}`);
    },
    install: (
      containerId: string,
      accountUpn: string,
      scopes: string,
      returnTo?: string,
    ) => {
      const params = new URLSearchParams({ containerId, accountUpn });
      if (scopes) params.set("scopes", scopes);
      if (returnTo) params.set("returnTo", returnTo);
      return request<InstallUrlResponse>(`/api/ms365/install?${params}`);
    },
    disconnect: (containerId: string) =>
      del<{ success: boolean }>(
        `/api/ms365/disconnect?containerId=${encodeURIComponent(containerId)}`,
      ),
  },
  // App sign-in is per Boo: its settings and the policies of its apps.
  sso: {
    settings: (containerId: string) =>
      request<SsoSettingsResponse>(`/api/sso/boos/${encodeURIComponent(containerId)}/settings`),
    saveSettings: (containerId: string, body: { enabledUpstreams: SsoUpstream[]; extraDomains: string[] }) =>
      put<{ data: SsoSettings }>(`/api/sso/boos/${encodeURIComponent(containerId)}/settings`, body),
    resetSettings: (containerId: string) =>
      del<{ data: SsoSettings }>(`/api/sso/boos/${encodeURIComponent(containerId)}/settings`),
    apps: (containerId: string) =>
      request<SsoAppsResponse>(`/api/sso/boos/${encodeURIComponent(containerId)}/apps`),
    updateApp: (containerId: string, appId: string, body: { name: string | null; description: string | null }) =>
      patch<{ data: { appId: string; name: string | null; description: string | null } }>(
        `/api/sso/boos/${encodeURIComponent(containerId)}/apps/${encodeURIComponent(appId)}`,
        body,
      ),
    savePolicy: (containerId: string, appId: string, body: SsoPolicyInput) =>
      put<{ data: SsoPolicy }>(
        `/api/sso/boos/${encodeURIComponent(containerId)}/apps/${encodeURIComponent(appId)}/policy`,
        body,
      ),
    // Who changed this Boo's sign-in, newest first. `before` is the previous page's nextBefore.
    audit: (containerId: string, params: { limit?: number; before?: string } = {}) => {
      const search = new URLSearchParams();
      if (params.limit) search.set("limit", String(params.limit));
      if (params.before) search.set("before", params.before);
      const qs = search.toString();
      return request<SsoAuditPage>(`/api/sso/boos/${encodeURIComponent(containerId)}/audit${qs ? `?${qs}` : ""}`);
    },
    adminOrg: (orgId: string) =>
      request<AdminSsoResponse>(`/api/admin/sso/${encodeURIComponent(orgId)}`),
  },
  codeHosts: {
    // Per Boo: what it can use plus the workspace pool. Without containerId:
    // the workspace pool (owners/admins).
    list: (containerId?: string) =>
      request<CodeHostsResponse>(`/api/code-hosts${containerId ? `?containerId=${encodeURIComponent(containerId)}` : ""}`),
    // GitHub: the install URL to send the browser to.
    githubInstall: (containerId: string | undefined, scope: CodeHostScope, returnTo?: string) => {
      const params = new URLSearchParams({ scope });
      if (containerId) params.set("containerId", containerId);
      if (returnTo) params.set("returnTo", returnTo);
      return request<InstallUrlResponse>(`/api/code-hosts/github/install?${params}`);
    },
    // GitLab service account: validate a pasted token; binds the only group or asks for one.
    gitlabConnect: (body: { containerId?: string; scope: CodeHostScope; baseUrl?: string; token: string; groupId?: string }) =>
      post<GitlabConnectResult>("/api/code-hosts/gitlab/connect", body),
    // GitLab OAuth: the authorize URL to send the browser to.
    gitlabOAuthStart: (body: { containerId?: string; scope: CodeHostScope; returnTo?: string; baseUrl?: string; clientId?: string; clientSecret?: string }) =>
      post<InstallUrlResponse>("/api/code-hosts/gitlab/oauth/start", body),
    groups: (connectionId: string) =>
      request<{ groups: GitlabGroupOption[]; currentGroupId: string | null }>(`/api/code-hosts/${encodeURIComponent(connectionId)}/groups`),
    setGroup: (connectionId: string, groupId: string) =>
      put<{ connection: CodeHostConnection }>(`/api/code-hosts/${encodeURIComponent(connectionId)}/group`, { groupId }),
    grant: (connectionId: string, containerId: string) =>
      post<{ ok: true }>(`/api/code-hosts/${encodeURIComponent(connectionId)}/grants`, { containerId }),
    revokeGrant: (connectionId: string, containerId: string) =>
      del<{ ok: true }>(`/api/code-hosts/${encodeURIComponent(connectionId)}/grants/${encodeURIComponent(containerId)}`),
    setDefault: (containerId: string, connectionId: string | null) =>
      put<{ defaultConnectionId: string | null }>("/api/code-hosts/boo/default", { containerId, connectionId }),
    promote: (connectionId: string) => post<{ ok: true; connection: CodeHostConnection }>(`/api/code-hosts/${encodeURIComponent(connectionId)}/promote`),
    remove: (connectionId: string) => del<{ ok: true }>(`/api/code-hosts/${encodeURIComponent(connectionId)}`),
  },
  gchat: {
    boo: {
      status: () => request<BooGchatStatus>("/api/gchat/boo/status"),
      install: (containerId?: string, returnTo?: string) => {
        const params = new URLSearchParams();
        if (containerId) params.set("containerId", containerId);
        if (returnTo) params.set("returnTo", returnTo);
        const qs = params.toString();
        return request<InstallUrlResponse>(
          `/api/gchat/boo/install${qs ? `?${qs}` : ""}`,
        );
      },
      disconnect: () => del<{ success: boolean }>("/api/gchat/boo/install"),
      move: (containerId: string) =>
        post<{ success: boolean }>("/api/gchat/boo/move", { containerId }),
      // The owner of the Boo the workspace answers through lets it go, so an
      // admin of another Boo can attach it.
      release: () => post<{ success: boolean }>("/api/gchat/boo/release"),
    },
    custom: {
      status: (containerId: string) =>
        request<GchatCustomStatus>(`/api/gchat/custom/install/${containerId}`),
      install: (
        containerId: string,
        serviceAccountJson: string,
        projectNumber: string,
      ) =>
        post<{ ok: boolean }>(`/api/gchat/custom/install/${containerId}`, {
          serviceAccountJson,
          projectNumber,
        }),
      disconnect: (containerId: string) =>
        del<{ ok: boolean }>(`/api/gchat/custom/install/${containerId}`),
      userOauth: {
        status: (containerId: string) =>
          request<GchatCustomUserOauthStatus>(
            `/api/gchat/custom/install/${containerId}/user-oauth`,
          ),
        saveClient: (
          containerId: string,
          clientId: string,
          clientSecret: string,
        ) =>
          post<{ ok: boolean; redirectUri: string | null }>(
            `/api/gchat/custom/install/${containerId}/user-oauth/client`,
            { clientId, clientSecret },
          ),
        disconnect: (containerId: string) =>
          del<{ ok: boolean }>(
            `/api/gchat/custom/install/${containerId}/user-oauth`,
          ),
      },
      admin: {
        state: (containerId: string) =>
          request<GchatWorkspaceAdminState>(
            `/api/gchat/custom/install/${containerId}/admin/state`,
          ),
        disconnect: (containerId: string) =>
          post<{ ok: boolean }>(
            `/api/gchat/custom/install/${containerId}/admin/disconnect`,
            {},
          ),
        visibility: (containerId: string) =>
          request<GchatWorkspaceVisibility>(
            `/api/gchat/custom/install/${containerId}/admin/visibility`,
          ),
      },
    },
  },
};
